import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents, Marker, Circle as LeafletCircle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Supercluster from "supercluster";
import {
  ArrowLeft, Globe2, Search, Layers, Loader2, MapPin, Plus,
  Edit3, Trash2, X, Save, Filter, Navigation, Crosshair, Copy, Check, KeyRound,
  FileText, CreditCard, Camera, Image as ImageIcon, CheckCircle2, XCircle, Circle,
  Download, WifiOff, HardDriveDownload, Calendar, LocateFixed, Locate, Minus,
  HandCoins, ZoomIn, Scissors,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  savePetaCustomers, getPetaCustomers, getPetaMeta,
  enqueueDeliveryEdit, computeCounts,
} from "@/utils/petaOfflineDB";
import {
  prefetchArea, countAreaTiles, getCachedTileCount,
} from "@/utils/tileCache";
import TurboDotsLayer from "@/components/TurboDotsLayer";
import ExportMapModal from "@/components/ExportMapModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Overlay marker colors — covers both Antar Surat and Tunggakan status vocabularies.
const OVERLAY_STATUS_COLORS = {
  delivered: "#10b981", not_found: "#94a3b8", promised_pay: "#0ea5e9", pending: "#ef4444",
  lunas: "#10b981", tidak_ketemu: "#94a3b8", janji_bayar: "#0ea5e9", ditagih: "#f59e0b",
  putus: "#a855f7",
};

// Label daftar status Antar Surat (dipakai untuk indikator filter yang dibawa dari daftar).
const AS_STATUS_LABEL = {
  pending: "Belum", delivered: "Sudah", not_found: "Tidak Ketemu",
  promised_pay: "Janji Bayar", lunas: "Lunas", putus: "Putus",
};

// Status Antar Surat yang menyimpan tanggal penetapan (status_date).
const DATED_STATUSES = ["delivered", "not_found", "lunas", "putus"];

// Opsi filter status Antar Surat — dipakai tombol filter langsung di peta.
const AS_STATUS_FILTER_OPTIONS = [
  { value: "", label: "Semua" },
  { value: "pending", label: "Belum" },
  { value: "delivered", label: "Sudah" },
  { value: "promised_pay", label: "Janji Bayar" },
  { value: "lunas", label: "Lunas" },
  { value: "putus", label: "Putus" },
  { value: "not_found", label: "Tidak Ketemu" },
];

// Default Tebo center
const DEFAULT_CENTER = [-1.4923, 102.4442];
const DEFAULT_ZOOM = 14;

// Persist/restore the last map view so returning to the app (e.g. when the
// mobile browser reloads a backgrounded tab) keeps the user's position & zoom
// instead of snapping back to the default view.
const SAVED_VIEW_KEY = "peta_last_view";
const SAVED_VIEW_MAX_AGE = 30 * 60 * 1000; // 30 menit
function readSavedView() {
  try {
    const v = JSON.parse(localStorage.getItem(SAVED_VIEW_KEY) || "null");
    if (!v || !Array.isArray(v.center) || v.center.length !== 2 || typeof v.zoom !== "number") return null;
    if (!v.ts || Date.now() - v.ts > SAVED_VIEW_MAX_AGE) return null;
    return v;
  } catch { return null; }
}
function saveView(map) {
  try {
    const c = map.getCenter();
    localStorage.setItem(SAVED_VIEW_KEY, JSON.stringify({ center: [c.lat, c.lng], zoom: map.getZoom(), ts: Date.now() }));
  } catch { /* ignore */ }
}

const baseLayers = {
  street: {
    name: "Jalan",
    urls: ["https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"],
    attribution: "© OpenStreetMap",
    maxZoom: 19,
    subdomains: ["a", "b", "c"],
  },
  satellite: {
    name: "Satelit",
    // Google Satellite tiles (clearer, same source as Google Maps)
    urls: ["https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"],
    attribution: "© Google",
    maxZoom: 21,
    subdomains: ["0", "1", "2", "3"],
  },
  hybrid: {
    name: "Hybrid",
    // Google Hybrid (satellite + roads + labels)
    urls: ["https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"],
    attribution: "© Google",
    maxZoom: 21,
    subdomains: ["0", "1", "2", "3"],
  },
};

const statusColor = (s) => {
  const v = String(s || "").toLowerCase();
  if (v.includes("non")) return "#ef4444"; // non aktif - red
  if (v.includes("aktif")) return "#10b981"; // aktif - green
  return "#64748b";
};

// Detect coarse pointer (touch device) — used to enlarge tap hit-area & disable heavy animations.
const IS_TOUCH = typeof window !== "undefined" && (
  ("ontouchstart" in window) ||
  (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
);
// Detect mobile-sized viewport (used to trim rendering on phones for smooth pan/zoom).
const IS_MOBILE = typeof window !== "undefined" && (
  IS_TOUCH || (window.innerWidth && window.innerWidth < 768)
);

// Compute marker radius based on zoom. On touch/mobile we use a finger-friendly floor
// so we can skip the second (invisible hit-area) CircleMarker per pin — a big perf win.
const radiusForZoom = (zoom) => {
  if (IS_TOUCH) {
    if (zoom >= 18) return 10;
    if (zoom >= 16) return 9;
    if (zoom >= 14) return 8;
    if (zoom >= 12) return 7;
    return 6;
  }
  if (zoom >= 18) return 9;
  if (zoom >= 16) return 7;
  if (zoom >= 14) return 5.5;
  if (zoom >= 12) return 4;
  return 3.5;
};

// Minimum hit-area radius (~44px Apple HIG / 48dp Material guideline → ~22px radius).
const TOUCH_HIT_RADIUS = 22;
const MOUSE_HIT_RADIUS = 14;

// Highlight icon used when "moving" a pin
const editIcon = L.divIcon({
  className: "peta-global-edit",
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#f59e0b;border:3px solid #fff;box-shadow:0 0 0 2px #d97706,0 2px 6px rgba(0,0,0,.5);"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

// Invisible anchor for the shared popup (no DOM weight, no default icon download)
const popupAnchorIcon = L.divIcon({
  className: "pg-popup-anchor",
  html: "",
  iconSize: [1, 1],
  iconAnchor: [0, 0],
});

// Reusable ID-label icon (lazy cached by id) — small pill above the marker, non-interactive.
const _idLabelCache = new Map();
function getIdLabelIcon(cid) {
  const key = String(cid || "");
  if (_idLabelCache.has(key)) return _idLabelCache.get(key);
  const icon = L.divIcon({
    className: "pg-id-label",
    html: `<div style="background:rgba(15,23,42,0.85);color:#fff;font-size:10px;font-weight:600;font-family:ui-monospace,monospace;padding:1px 5px;border-radius:6px;border:1px solid rgba(255,255,255,0.7);white-space:nowrap;pointer-events:none;box-shadow:0 1px 2px rgba(0,0,0,0.4);transform:translate(-50%,-16px);display:inline-block;">${key}</div>`,
    iconSize: [1, 1],
    iconAnchor: [0, 0],
  });
  _idLabelCache.set(key, icon);
  return icon;
}

// Google Maps-style "you are here" blue dot with pulsing outer ring.
// Injected once at module load.
if (typeof document !== "undefined" && !document.getElementById("pg-gps-style")) {
  const style = document.createElement("style");
  style.id = "pg-gps-style";
  style.textContent = `
    @keyframes pgPulse {
      0%   { transform: scale(1);   opacity: 0.6; }
      70%  { transform: scale(2.4); opacity: 0;   }
      100% { transform: scale(2.4); opacity: 0;   }
    }
    .pg-gps-dot {
      position: relative;
      width: 18px; height: 18px;
      border-radius: 50%;
      background: #1a73e8;
      border: 3px solid #ffffff;
      box-shadow: 0 0 0 1px rgba(0,0,0,.18), 0 2px 6px rgba(0,0,0,.35);
    }
    .pg-gps-dot::before {
      content: "";
      position: absolute; inset: -3px;
      border-radius: 50%;
      background: #1a73e8;
      opacity: 0.45;
      animation: pgPulse 2.2s ease-out infinite;
      z-index: -1;
    }
    .pg-gps-arrow {
      position: absolute;
      left: 50%; top: 50%;
      width: 0; height: 0;
      border-left: 9px solid transparent;
      border-right: 9px solid transparent;
      border-bottom: 14px solid #1a73e8;
      transform-origin: 50% 100%;
      transform: translate(-50%, -100%) rotate(var(--pg-heading, 0deg));
      filter: drop-shadow(0 0 1px #fff) drop-shadow(0 0 2px rgba(0,0,0,.3));
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
}

const makeGpsIcon = (heading) => L.divIcon({
  className: "pg-gps-marker",
  html: heading != null
    ? `<div class="pg-gps-dot"><span class="pg-gps-arrow" style="--pg-heading:${heading}deg"></span></div>`
    : `<div class="pg-gps-dot"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Cluster bubble as a DOM divIcon — much cheaper than CircleMarker + permanent Tooltip.
// Cached so identical counts reuse the same icon instance.
const clusterIconCache = new Map();
function getClusterIcon(count) {
  const cached = clusterIconCache.get(count);
  if (cached) return cached;
  const size = Math.min(52, 24 + Math.log10(count + 1) * 12);
  const fill = count > 500 ? "#0ea5e9" : count > 100 ? "#06b6d4" : count > 30 ? "#22d3ee" : "#67e8f9";
  const font = Math.max(11, Math.round(size * 0.36));
  const icon = L.divIcon({
    className: "pg-cluster",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${fill};border:2px solid #fff;box-shadow:0 0 0 1px rgba(2,132,199,.35),0 2px 6px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${font}px;color:#0c4a6e;">${count.toLocaleString()}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
  // Limit cache growth
  if (clusterIconCache.size > 200) clusterIconCache.clear();
  clusterIconCache.set(count, icon);
  return icon;
}

function ClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick && onClick(e.latlng) });
  return null;
}

function ViewportTracker({ onChange }) {
  const map = useMap();
  useEffect(() => {
    let timer = null;
    const fire = () => { onChange(map.getBounds(), map.getZoom()); saveView(map); };
    // Slightly longer debounce on touch devices — avoids re-rendering hundreds
    // of markers mid-pan on lower-end phones.
    const wait = IS_TOUCH ? 180 : 90;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, wait);
    };
    // Only react to *end* events — avoids re-render storms during pan/zoom.
    map.on("moveend", debounced);
    map.on("zoomend", debounced);
    // Initial fire (immediate so the first viewport is captured ASAP)
    fire();
    return () => {
      map.off("moveend", debounced);
      map.off("zoomend", debounced);
      if (timer) clearTimeout(timer);
    };
  }, [map, onChange]);
  return null;
}

function MapRefCapture({ onReady }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  }, [map, onReady]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, Math.max(map.getZoom(), 17), { duration: 0.6 });
  }, [target, map]);
  return null;
}

export default function PetaGlobal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const antarSuratId = searchParams.get("antar_surat") || "";
  const tunggakanId = searchParams.get("tunggakan") || "";
  const minBulanParam = searchParams.get("min_bulan");
  const maxBulanParam = searchParams.get("max_bulan");
  const asStatusParam = searchParams.get("as_status") || "";
  const focusCid = searchParams.get("focus") || "";
  const focusDoneRef = useRef(false);
  const userToken = localStorage.getItem("user_token");
  const adminToken = localStorage.getItem("admin_token");
  const token = userToken || adminToken;
  const isAdmin = !!adminToken && !userToken;
  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
  const isReadOnly = userRole === "petugas_lapangan" && !!userToken;

  // Overlay state (Antar Surat OR Tunggakan) — shared markers/effectiveCustomers machinery
  const [deliveryList, setDeliveryList] = useState(null); // {id, name, items[], counts}
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // "antar_surat" | "tunggakan"

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ wilayah: "", rayon: "", kecamatan: "", status: "" });
  const [facets, setFacets] = useState({ wilayah: [], rayon: [], kecamatan: [], golongan: [] });
  const [base, setBase] = useState("satellite");
  const [showFilter, setShowFilter] = useState(false);

  // Filter status Antar Surat aktif di peta — diinisialisasi dari URL, bisa diubah langsung di peta.
  const [asStatusFilter, setAsStatusFilter] = useState(asStatusParam);
  useEffect(() => { setAsStatusFilter(asStatusParam); }, [asStatusParam]);

  // Add/Edit modal
  const [pickingForNew, setPickingForNew] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit'
  const [modalData, setModalData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  // Persistent dialog for displaying newly generated admin code
  const [generatedCode, setGeneratedCode] = useState(null); // { code, expires_at, customer_id, nama }
  const [codeCopied, setCodeCopied] = useState(false);

  // Inline editing GPS: drag-style not used; user uses "Pindahkan pin" mode.
  const [movingId, setMovingId] = useState(null);

  // Shared popup: only ONE popup mounts (for selectedPin) instead of one per visible marker.
  // Massive perf win for viewports with hundreds of points.
  const [selectedPin, setSelectedPin] = useState(null); // customer object

  // Inline "Antar Surat" status editor (status/note/photo) opened straight from a map pin.
  const [editDeliveryItem, setEditDeliveryItem] = useState(null);

  // Apply a delivery-item update (status/note/photo + counts) into local deliveryList state.
  const handleDeliveryUpdated = useCallback((updatedItem, newCounts) => {
    setDeliveryList((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).map((it) =>
        it.customer_id === updatedItem.customer_id ? { ...it, ...updatedItem } : it
      );
      return { ...prev, items, counts: newCounts || computeCounts(items) };
    });
  }, []);

  // Jump straight to the PDAM Tebo bill-payment page with the ID pre-filled & auto-checked.
  // Pass from=peta&focus so that after paying, the user can return to this exact pin
  // (map flies back to it instead of resetting to the default zoom).
  const goToPayment = useCallback((cid) => {
    navigate(`/pdam-tebo?id=${encodeURIComponent(cid)}&from=peta&focus=${encodeURIComponent(cid)}`);
  }, [navigate]);

  // Offline state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showOfflineDl, setShowOfflineDl] = useState(false);
  const [offlineMeta, setOfflineMeta] = useState(null);

  // Bulk sync from PDAM Online state
  const [showBulkSync, setShowBulkSync] = useState(false);

  // Bulk photo upload (match by filename = customer ID)
  const [showBulkPhoto, setShowBulkPhoto] = useState(false);

  // Export peta (JPG / PDF)
  const [showExport, setShowExport] = useState(false);

  // Show ID labels above each pin (persisted). Useful for meter readers.
  const [showIdLabels, setShowIdLabels] = useState(() => {
    try { return localStorage.getItem("peta_show_id_labels") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("peta_show_id_labels", showIdLabels ? "1" : "0"); } catch { /* ignore */ }
  }, [showIdLabels]);

  // Turbo Dot mode: renders all pins on a single canvas layer instead of hundreds of
  // React <CircleMarker> nodes. On mobile with many pins this is the difference between
  // 5-10fps and buttery-smooth panning. Persisted per-device and defaults to ON for touch.
  const [turboDots, setTurboDots] = useState(() => {
    try {
      const v = localStorage.getItem("peta_turbo_dots");
      if (v === "1") return true;
      if (v === "0") return false;
    } catch { /* ignore */ }
    return IS_MOBILE; // default: enable on mobile / touch, off on desktop
  });
  useEffect(() => {
    try { localStorage.setItem("peta_turbo_dots", turboDots ? "1" : "0"); } catch { /* ignore */ }
  }, [turboDots]);

  // Calon Pelanggan (prospective customers) — yellow pins, separate collection
  const [calonList, setCalonList] = useState([]);
  const [pickingCalon, setPickingCalon] = useState(false);
  const [calonModal, setCalonModal] = useState(null); // { mode:'add'|'edit', data }
  const [selectedCalon, setSelectedCalon] = useState(null);

  // Global edit toggle (admin-controlled). OFF = no one can edit/move/delete pins.
  // When ON, ANY role (petugas/kasir/petugas lapangan/admin) can edit freely.
  const [petaEditEnabled, setPetaEditEnabled] = useState(false);
  const canEdit = petaEditEnabled;
  // Inline bill check (tombol "Cek" di popup) — tampil tanpa pindah ke halaman bayar
  const [popupBill, setPopupBill] = useState({}); // { [cid]: {loading, count, total, nama, error} }

  // Map ref (declared early because GPS hooks below reference it).
  const mapRef = useRef(null);
  const onMapReady = useCallback((m) => { mapRef.current = m; }, []);

  // === Real-time GPS tracking (Google Maps style blue dot) ===
  // gpsPos: {lat, lng, accuracy, heading?} | null
  const [gpsPos, setGpsPos] = useState(null);
  // 'off' = no tracking, 'on' = tracking but free pan, 'follow' = tracking + map recenters
  // Default 'on' so opening the map auto-centers on the user's location (no manual zoom).
  const [gpsMode, setGpsMode] = useState("on");
  const gpsWatchRef = useRef(null);
  // Restore the last saved view (if recent) so switching apps & returning
  // doesn't reset the zoom/position.
  const savedViewRef = useRef(readSavedView());
  // When we restored a view, suppress the GPS "first fix" auto-fly so it doesn't
  // override the user's previous position/zoom.
  const gpsFirstFixRef = useRef(!!savedViewRef.current);

  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    getPetaMeta().then(setOfflineMeta);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Start/stop geolocation watcher based on gpsMode.
  useEffect(() => {
    if (gpsMode === "off") {
      if (gpsWatchRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(gpsWatchRef.current);
        gpsWatchRef.current = null;
      }
      gpsFirstFixRef.current = false;
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Perangkat tidak mendukung GPS.");
      setGpsMode("off");
      return;
    }
    // Only start a new watcher if one isn't already running.
    if (gpsWatchRef.current == null) {
      gpsWatchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const next = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy || 0,
            heading: (pos.coords.heading != null && !Number.isNaN(pos.coords.heading)) ? pos.coords.heading : null,
          };
          setGpsPos(next);
          // First fix → always fly to user's location (wait until the map is ready).
          if (!gpsFirstFixRef.current && mapRef.current) {
            gpsFirstFixRef.current = true;
            mapRef.current.flyTo([next.lat, next.lng], Math.max(mapRef.current.getZoom(), 17), { duration: 0.6 });
            try { navigator.vibrate && navigator.vibrate(15); } catch { /* noop */ }
          }
        },
        (err) => {
          const msg = err.code === 1 ? "Izinkan akses lokasi di browser HP Anda."
            : err.code === 2 ? "Lokasi tidak tersedia. Pastikan GPS aktif."
            : err.code === 3 ? "Pencarian sinyal GPS timeout."
            : "Gagal mengambil lokasi GPS.";
          toast.error(msg);
          setGpsMode("off");
        },
        { enableHighAccuracy: true, maximumAge: 1500, timeout: 20000 }
      );
    }
    return () => {
      // Don't clear here — cleanup happens when gpsMode becomes 'off'.
    };
  }, [gpsMode]);

  // Follow mode: recenter map every time GPS updates (smooth pan).
  useEffect(() => {
    if (gpsMode !== "follow" || !gpsPos || !mapRef.current) return;
    mapRef.current.panTo([gpsPos.lat, gpsPos.lng], { animate: true, duration: 0.4 });
  }, [gpsMode, gpsPos]);

  // If user drags the map while in follow mode, drop back to plain tracking
  // so the map doesn't fight the user.
  useEffect(() => {
    if (gpsMode !== "follow") return;
    const m = mapRef.current;
    if (!m) return;
    const onDragStart = () => setGpsMode("on");
    m.on("dragstart", onDragStart);
    return () => { m.off("dragstart", onDragStart); };
  }, [gpsMode]);

  // Stop the watcher when the page unmounts.
  useEffect(() => () => {
    if (gpsWatchRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
    }
  }, []);

  // Cycle GPS button: off → on → follow → off
  const toggleGps = useCallback(() => {
    setGpsMode((m) => {
      if (m === "off") return "on";
      if (m === "on") return "follow";
      return "off";
    });
  }, []);

  // Viewport tracking — only render markers in current view for performance
  const [viewport, setViewport] = useState({ bounds: null, zoom: DEFAULT_ZOOM });
  const rafRef = useRef(null);
  const onViewportChange = useCallback((bounds, zoom) => {
    // Throttle via RAF to avoid running on every pixel of pan
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setViewport({ bounds, zoom });
    });
  }, []);

  // Overlay fetch — Antar Surat or Tunggakan list when query param present
  useEffect(() => {
    const oid = tunggakanId || antarSuratId;
    if (!oid || !token) { setDeliveryList(null); setOverlayMode(null); return; }
    const mode = tunggakanId ? "tunggakan" : "antar_surat";
    const endpoint = tunggakanId ? "tunggakan-lists" : "delivery-lists";
    let cancelled = false;
    (async () => {
      setLoadingDelivery(true);
      try {
        const res = await axios.get(`${API}/${endpoint}/${oid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) { setDeliveryList(res.data); setOverlayMode(mode); }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Gagal memuat daftar.");
          setDeliveryList(null); setOverlayMode(null);
        }
      } finally {
        if (!cancelled) setLoadingDelivery(false);
      }
    })();
    return () => { cancelled = true; };
  }, [antarSuratId, tunggakanId, token]);

  // Antar Surat month-range filter (jumlah_tagihan) passed from the list via URL.
  const monthRange = useMemo(() => {
    const min = minBulanParam === null || minBulanParam === "" ? null : Number(minBulanParam);
    const max = maxBulanParam === null || maxBulanParam === "" ? null : Number(maxBulanParam);
    return { min, max, active: min !== null || max !== null };
  }, [minBulanParam, maxBulanParam]);

  const passesMonthFilter = useCallback((it) => {
    if (overlayMode !== "antar_surat") return true;
    // Status filter carried over from the Antar Surat list (e.g. only "belum").
    if (asStatusFilter && (it?.status || "pending") !== asStatusFilter) return false;
    if (!monthRange.active) return true;
    const j = it?.jumlah_tagihan;
    if (typeof j !== "number") return false;
    if (monthRange.min !== null && j < monthRange.min) return false;
    if (monthRange.max !== null && j > monthRange.max) return false;
    return true;
  }, [monthRange, overlayMode, asStatusFilter]);

  // Map: customer_id -> delivery item (status, note, photo) for color coding
  const deliveryItemMap = useMemo(() => {
    const m = new Map();
    if (!deliveryList?.items) return m;
    for (const it of deliveryList.items) {
      if (!passesMonthFilter(it)) continue;
      m.set(it.customer_id, it);
    }
    return m;
  }, [deliveryList, passesMonthFilter]);

  // In delivery-list mode: prefer delivery items (with snapshot coords) overlaid
  // with fresh `customers` data when available. Pin set is restricted to IDs in
  // the list so officer only sees their delivery targets on the map.
  const effectiveCustomers = useMemo(() => {
    if (!deliveryList) return customers;
    const freshById = new Map();
    for (const c of customers) freshById.set(c.customer_id, c);
    const out = [];
    for (const it of (deliveryList.items || [])) {
      if (!passesMonthFilter(it)) continue;
      const fresh = freshById.get(it.customer_id);
      if (fresh && fresh.latitude != null && fresh.longitude != null) {
        out.push(fresh);
      } else if (it.latitude != null && it.longitude != null) {
        out.push({
          customer_id: it.customer_id,
          nama: it.nama || "",
          alamat: it.alamat || "",
          latitude: it.latitude,
          longitude: it.longitude,
          status: "Aktif",
        });
      }
    }
    return out;
  }, [customers, deliveryList, passesMonthFilter]);

  // Bounds covering ONLY this Antar Surat list's points — used to scope the offline
  // map download so just the delivery targets' coordinates/area are saved.
  const antarBounds = useMemo(() => {
    if (overlayMode !== "antar_surat") return null;
    const pts = effectiveCustomers.filter((c) => c.latitude != null && c.longitude != null);
    if (!pts.length) return null;
    let north = -90, south = 90, east = -180, west = 180;
    for (const c of pts) {
      const lat = Number(c.latitude), lng = Number(c.longitude);
      north = Math.max(north, lat); south = Math.min(south, lat);
      east = Math.max(east, lng); west = Math.min(west, lng);
    }
    const padLat = (north - south) * 0.15 || 0.01;
    const padLng = (east - west) * 0.15 || 0.01;
    return { north: north + padLat, south: south - padLat, east: east + padLng, west: west - padLng };
  }, [overlayMode, effectiveCustomers]);

  // Real-time overlay refresh (~5s) for Antar Surat on the map: when a shared member
  // changes a status/photo, refetch the overlay so pin colours & photos stay live.
  const overlayUpdatedRef = useRef(null);
  useEffect(() => { if (deliveryList?.updated_at) overlayUpdatedRef.current = deliveryList.updated_at; }, [deliveryList?.updated_at]);
  useEffect(() => {
    if (overlayMode !== "antar_surat" || !antarSuratId || !token) return;
    let stop = false;
    const id = setInterval(async () => {
      if (stop || !navigator.onLine || document.hidden) return;
      try {
        const st = await axios.get(`${API}/delivery-lists/${antarSuratId}/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const srv = st.data?.updated_at;
        if (srv && srv !== overlayUpdatedRef.current) {
          overlayUpdatedRef.current = srv;
          const res = await axios.get(`${API}/delivery-lists/${antarSuratId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setDeliveryList(res.data);
        }
      } catch { /* ignore polling errors */ }
    }, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [overlayMode, antarSuratId, token]);

  // Auto-open the offline download modal (scoped to this list) when arriving with ?offline=1.
  const offlineAutoOpenedRef = useRef(false);
  useEffect(() => {
    if ((searchParams.get("offline") || "") !== "1" || offlineAutoOpenedRef.current) return;
    if (overlayMode !== "antar_surat" || !antarBounds) return;
    offlineAutoOpenedRef.current = true;
    if (mapRef.current) {
      try {
        mapRef.current.fitBounds(
          [[antarBounds.south, antarBounds.west], [antarBounds.north, antarBounds.east]],
          { padding: [40, 40] }
        );
      } catch { /* noop */ }
    }
    setShowOfflineDl(true);
  }, [searchParams, overlayMode, antarBounds]);

  // Build supercluster index whenever effectiveCustomers change.
  // On mobile/touch we cluster more aggressively so far fewer individual pins
  // are rendered until the user really zooms in — critical for smooth pan/zoom.
  const clusterIndex = useMemo(() => {
    const idx = new Supercluster({
      radius: IS_MOBILE ? 90 : 60,        // cluster radius in pixels
      maxZoom: IS_MOBILE ? 16 : 17,       // beyond this, all points are individual
      minPoints: IS_MOBILE ? 2 : 3,       // ≥N points form a cluster
      nodeSize: 64,
    });
    const features = [];
    for (const c of effectiveCustomers) {
      if (c.latitude == null || c.longitude == null) continue;
      features.push({
        type: "Feature",
        properties: { cid: c.customer_id, idx: features.length },
        geometry: { type: "Point", coordinates: [c.longitude, c.latitude] },
      });
    }
    idx.load(features);
    return idx;
  }, [effectiveCustomers]);

  // Build a quick lookup for customer data by id
  const customerById = useMemo(() => {
    const m = new Map();
    for (const c of effectiveCustomers) m.set(c.customer_id, c);
    return m;
  }, [effectiveCustomers]);

  // Visible items = clusters + individual points within viewport
  const visibleItems = useMemo(() => {
    if (!viewport.bounds) return { clusters: [], points: [] };
    const b = viewport.bounds;
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const z = Math.floor(viewport.zoom);
    const items = clusterIndex.getClusters(bbox, z);
    const clusters = [];
    const points = [];
    for (const it of items) {
      if (it.properties.cluster) clusters.push(it);
      else points.push(it);
    }
    return { clusters, points };
  }, [clusterIndex, viewport]);

  const markerRadius = useMemo(() => radiusForZoom(viewport.zoom), [viewport.zoom]);

  // Precomputed dot descriptors for the Turbo canvas layer — one entry per visible
  // individual point, coloured per Antar Surat / Tunggakan status when applicable.
  const turboPoints = useMemo(() => {
    if (!turboDots) return [];
    const out = [];
    for (const pt of visibleItems.points) {
      const cid = pt.properties.cid;
      const c = customerById.get(cid);
      if (!c || c.latitude == null || c.longitude == null) continue;
      if (movingId === cid) continue; // handled by the amber edit marker
      const deliveryItem = deliveryItemMap.get(cid);
      const color = deliveryItem
        ? (OVERLAY_STATUS_COLORS[deliveryItem.status] || "#ef4444")
        : statusColor(c.status);
      const r = deliveryItem ? Math.max(markerRadius, 7) : markerRadius;
      out.push({
        cid,
        lat: Number(c.latitude),
        lng: Number(c.longitude),
        color,
        radius: r,
        strokeColor: "#ffffff",
        strokeWidth: deliveryItem ? 2 : 1.2,
      });
    }
    return out;
  }, [turboDots, visibleItems.points, customerById, deliveryItemMap, movingId, markerRadius]);

  const turboEnabled = turboDots && !pickingForNew && !pickingCalon && !movingId;
  const onTurboSelect = useCallback((cid) => {
    const c = customerById.get(cid);
    if (c) setSelectedPin(c);
  }, [customerById]);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Inline bill check for the popup "Cek" button (no navigation to payment page).
  const checkBillInline = useCallback(async (cid) => {
    setPopupBill((prev) => ({ ...prev, [cid]: { loading: true } }));
    try {
      const res = await axios.post(`${API}/pdam-tebo/check`, { no_sambungan: cid, nama: "" }, auth);
      const bills = res.data?.bills || [];
      const total = bills.reduce((s, b) => s + (Number(b.total) || 0), 0);
      setPopupBill((prev) => ({
        ...prev,
        [cid]: { loading: false, count: bills.length, total, nama: res.data?.pelanggan?.nama || "" },
      }));
    } catch (err) {
      setPopupBill((prev) => ({
        ...prev,
        [cid]: { loading: false, error: err?.response?.data?.detail || "Gagal cek tagihan." },
      }));
    }
  }, [auth]);

  const fetchCustomers = useCallback(async ({ flyToFirst = false } = {}) => {
    if (!token) {
      navigate("/login");
      return [];
    }
    setLoading(true);
    try {
      const params = { limit: 100000, only_with_gps: true };
      const q = search.trim();
      if (q) params.search = q;
      if (filter.wilayah) params.wilayah = filter.wilayah;
      if (filter.rayon) params.rayon = filter.rayon;
      if (filter.kecamatan) params.kecamatan = filter.kecamatan;
      if (filter.status) params.status = filter.status;
      const res = await axios.get(`${API}/peta-global/customers`, { ...auth, params });
      const list = res.data?.customers || [];
      setCustomers(list);
      setTotal(res.data?.total || 0);

      // Cache the FULL unfiltered set for offline use & faster next load
      if (!q && !filter.wilayah && !filter.rayon && !filter.kecamatan && !filter.status) {
        savePetaCustomers(list).then((n) => {
          if (n) getPetaMeta().then(setOfflineMeta);
        });
      }

      if (flyToFirst && q) {
        // Find best matching customer with GPS — prefer exact ID match, else first with coords
        const ql = q.toLowerCase();
        const withGps = list.filter((c) => c.latitude != null && c.longitude != null);
        const exact = withGps.find((c) => String(c.customer_id || "").toLowerCase() === ql);
        const target = exact || withGps[0];
        if (target) {
          setFlyTarget([Number(target.latitude), Number(target.longitude)]);
          toast.success(`Menuju lokasi: ${target.customer_id} — ${target.nama || "-"}`);
        } else {
          toast.error("Hasil pencarian tidak memiliki koordinat GPS.");
        }
      }
      return list;
    } catch (err) {
      if (err?.response?.status === 401) {
        // Do NOT auto-navigate/clear on 401. Users only log out via "Keluar".
        toast.error(err?.response?.data?.detail || "Gagal memuat data pelanggan (unauthorized).");
      } else {
        // Network error / offline → fall back to cached pins if available
        const cached = await getPetaCustomers();
        if (cached.length > 0) {
          setCustomers(cached);
          setTotal(cached.length);
          toast.info(`Mode offline: ${cached.length.toLocaleString()} titik dimuat dari penyimpanan.`);
          return cached;
        }
        toast.error(err?.response?.data?.detail || "Gagal memuat data pelanggan.");
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [auth, token, search, filter, navigate, isAdmin]);

  const fetchFacets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/peta-global/facets`, auth);
      setFacets(res.data || { wilayah: [], rayon: [], kecamatan: [], golongan: [] });
    } catch (err) {
      // silent
    }
  }, [auth]);

  const fetchCalon = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/calon-pelanggan`, auth);
      setCalonList(res.data?.items || []);
    } catch (err) {
      // silent
    }
  }, [auth, token]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => { fetchFacets(); }, [fetchFacets]);
  useEffect(() => { fetchCalon(); }, [fetchCalon]);

  // Instant first paint: show cached pins from IndexedDB while the network list
  // loads. Only fills in if nothing is loaded yet (network result still wins).
  useEffect(() => {
    let alive = true;
    getPetaCustomers().then((cached) => {
      if (alive && cached && cached.length) {
        setCustomers((prev) => (prev && prev.length ? prev : cached));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Load global edit toggle (admin-controlled ON/OFF).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/peta-global/edit-mode`, auth);
        if (alive) setPetaEditEnabled(!!res.data?.enabled);
      } catch { /* default stays OFF */ }
    })();
    return () => { alive = false; };
  }, [auth]);

  // Fokus otomatis ke pelanggan tertentu (dibuka via tombol "Lihat (Satelit)").
  useEffect(() => {
    if (!focusCid || focusDoneRef.current || customers.length === 0) return;
    const target = customers.find(
      (c) => String(c.customer_id || "").toLowerCase() === focusCid.toLowerCase()
    );
    if (target && target.latitude != null && target.longitude != null) {
      focusDoneRef.current = true;
      setFlyTarget([Number(target.latitude), Number(target.longitude)]);
      setSelectedPin(target);
      toast.success(`Menuju lokasi: ${target.customer_id} — ${target.nama || "-"}`);
    }
  }, [focusCid, customers]);

  const onSubmitSearch = (e) => { e.preventDefault(); fetchCustomers({ flyToFirst: true }); };

  const onMapClick = (latlng) => {
    if (pickingCalon) {
      setCalonModal({
        mode: "add",
        data: {
          nama: "", customer_id: "", alamat: "", nomor_telp: "", note: "",
          photo: "", _photo_new: "", status: "calon",
          latitude: latlng.lat, longitude: latlng.lng,
        },
      });
      setPickingCalon(false);
      return;
    }
    if (pickingForNew) {
      setModalMode("add");
      setModalData({
        customer_id: "",
        nama: "",
        alamat: "",
        kecamatan: "",
        wilayah: "",
        rayon: "",
        golongan: "",
        status: "Aktif",
        nomor_telp: "",
        latitude: latlng.lat,
        longitude: latlng.lng,
      });
      setPickingForNew(false);
      return;
    }
    if (movingId) {
      // Update existing customer's coords
      moveCustomer(movingId, latlng.lat, latlng.lng);
    }
  };

  const moveCustomer = async (cid, lat, lng) => {
    const cust = customers.find(c => c.customer_id === cid);
    if (!cust) { setMovingId(null); return; }
    try {
      const payload = { ...cust, latitude: lat, longitude: lng };
      await axios.put(`${API}/peta-global/customer/${encodeURIComponent(cid)}`, payload, auth);
      toast.success(`Pin ${cid} dipindahkan.`);
      setCustomers(prev => prev.map(c => c.customer_id === cid ? { ...c, latitude: lat, longitude: lng } : c));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memindahkan pin.");
    } finally {
      setMovingId(null);
    }
  };

  const openEdit = async (cust) => {
    setModalMode("edit");
    // Fetch full data (includes photo + update_code_info)
    try {
      const res = await axios.get(`${API}/peta-global/customer/${encodeURIComponent(cust.customer_id)}`, auth);
      setModalData({ ...cust, ...res.data, update_code: "" });
    } catch (err) {
      setModalData({ ...cust, update_code: "" });
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setModalData(null);
  };

  const saveModal = async () => {
    if (!modalData?.customer_id?.trim()) {
      toast.error("ID Pelanggan wajib diisi."); return;
    }
    if (modalData.latitude == null || modalData.longitude == null) {
      toast.error("Koordinat wajib (klik peta atau isi manual)."); return;
    }
    setSaving(true);
    try {
      const cid = modalData.customer_id.trim();
      const { _photo_new, ...payloadRest } = modalData;
      if (modalMode === "add") {
        const res = await axios.post(`${API}/peta-global/customer`, payloadRest, auth);
        toast.success(res.data?.message || "Tersimpan.");
        // Save photo separately if user attached one
        if (_photo_new) {
          try {
            await axios.post(
              `${API}/peta-global/customer/${encodeURIComponent(cid)}/photo`,
              { photo: _photo_new, update_code: payloadRest.update_code || "" },
              auth
            );
          } catch (errPhoto) {
            toast.error("Data tersimpan, tapi foto gagal: " + (errPhoto?.response?.data?.detail || ""));
          }
        }
      } else {
        await axios.put(`${API}/peta-global/customer/${encodeURIComponent(cid)}`, payloadRest, auth);
        if (_photo_new) {
          await axios.post(
            `${API}/peta-global/customer/${encodeURIComponent(cid)}/photo`,
            { photo: _photo_new, update_code: payloadRest.update_code || "" },
            auth
          );
        }
        toast.success("Pelanggan diperbarui.");
      }
      closeModal();
      fetchCustomers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async (cid) => {
    if (!canEdit) { toast.error("Edit Peta Global sedang dinonaktifkan admin."); return; }
    if (!window.confirm(`Hapus titik ${cid} dari peta?`)) return;
    try {
      await axios.delete(`${API}/peta-global/customer/${encodeURIComponent(cid)}`, auth);
      toast.success("Titik pelanggan dihapus.");
      setCustomers(prev => prev.filter(c => c.customer_id !== cid));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal hapus.");
    }
  };

  const generateUpdateCode = async (cid) => {
    if (!isAdmin) return;
    try {
      const res = await axios.post(
        `${API}/admin/peta-global/${encodeURIComponent(cid)}/generate-update-code`,
        {},
        auth
      );
      const code = res.data?.code;
      const expiresAt = res.data?.expires_at;
      const cust = customerById.get(cid);
      // If modal is open for this customer, update its update_code_info
      setModalData((prev) =>
        prev && prev.customer_id === cid
          ? { ...prev, update_code_info: { code, expires_at: expiresAt } }
          : prev
      );
      // Show persistent dialog so admin can copy code at leisure
      setCodeCopied(false);
      setGeneratedCode({
        code,
        expires_at: expiresAt,
        customer_id: cid,
        nama: cust?.nama || "-",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat kode.");
    }
  };

  const copyGeneratedCode = async () => {
    if (!generatedCode?.code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(generatedCode.code);
      } else {
        // Fallback for older browsers / non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = generatedCode.code;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCodeCopied(true);
      toast.success("Kode disalin ke clipboard");
      setTimeout(() => setCodeCopied(false), 2500);
    } catch (err) {
      toast.error("Gagal menyalin. Salin manual: " + generatedCode.code);
    }
  };

  const clearFilters = () => {
    setFilter({ wilayah: "", rayon: "", kecamatan: "", status: "" });
    setSearch("");
    setTimeout(fetchCustomers, 0);
  };

  const saveCalon = async () => {
    const d = calonModal?.data;
    if (!d) return;
    if (d.latitude == null || d.longitude == null) { toast.error("Titik lokasi wajib."); return; }
    const status = (d.status || "calon").toLowerCase();
    if (status === "aktif" && !String(d.customer_id || "").trim()) {
      toast.error("ID Pelanggan wajib diisi untuk status Aktif."); return;
    }
    const payload = {
      nama: d.nama || "", customer_id: (d.customer_id || "").trim(),
      alamat: d.alamat || "", nomor_telp: d.nomor_telp || "", note: d.note || "",
      latitude: d.latitude, longitude: d.longitude, status,
    };
    if (d._photo_new) payload.photo = d._photo_new;
    try {
      let res;
      if (calonModal.mode === "add") {
        res = await axios.post(`${API}/calon-pelanggan`, payload, auth);
      } else {
        res = await axios.put(`${API}/calon-pelanggan/${encodeURIComponent(d.id)}`, payload, auth);
      }
      toast.success(res.data?.message || "Tersimpan.");
      setCalonModal(null);
      setSelectedCalon(null);
      fetchCalon();
      if (res.data?.promoted) fetchCustomers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan calon.");
    }
  };

  const deleteCalon = async (id) => {
    if (!window.confirm("Hapus calon pelanggan ini?")) return;
    try {
      await axios.delete(`${API}/calon-pelanggan/${encodeURIComponent(id)}`, auth);
      toast.success("Calon dihapus.");
      setSelectedCalon(null);
      setCalonList(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal hapus.");
    }
  };

  const goToCustomer = (cust) => {
    if (cust.latitude != null && cust.longitude != null) {
      setFlyTarget([cust.latitude, cust.longitude]);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="peta-global-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-[1000]">
        <div className="px-3 py-2 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Kembali
          </Button>
          <div className="flex items-center gap-2">
            <Globe2 className="w-5 h-5 text-emerald-600" />
            <span className="font-semibold text-slate-800">Peta Global</span>
            <span className="hidden sm:inline text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
              {visibleItems.points.length.toLocaleString()} pin + {visibleItems.clusters.length.toLocaleString()} cluster / {customers.length.toLocaleString()} dimuat / {total.toLocaleString()} total
            </span>
            {isOffline && (
              <span className="text-[11px] px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full inline-flex items-center gap-1" data-testid="offline-badge">
                <WifiOff className="w-3 h-3" /> Offline
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!isReadOnly && (
              <Button
                size="sm"
                variant={pickingForNew ? "default" : "outline"}
                onClick={() => { setPickingForNew(v => !v); setMovingId(null); setPickingCalon(false); }}
                className={pickingForNew ? "gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" : "gap-1"}
                data-testid="toggle-add-btn"
                title="Tambah pelanggan baru dengan klik peta"
              >
                <Plus className="w-4 h-4" /> {pickingForNew ? "Klik di peta..." : "Tambah"}
              </Button>
            )}
            {isReadOnly && (
              <span className="text-[11px] px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid="readonly-badge">
                🔒 Mode Lihat Saja
              </span>
            )}
            <Button size="sm" variant="outline" onClick={() => setShowFilter(v => !v)} className="gap-1" data-testid="toggle-filter-btn">
              <Filter className="w-4 h-4" /> Filter
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowOfflineDl(true)}
              className="gap-1 border-sky-300 text-sky-700 hover:bg-sky-50"
              data-testid="open-offline-download-btn"
              title="Simpan peta & titik untuk dipakai offline"
            >
              <Download className="w-4 h-4" /> Offline
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(deliveryList ? `/antar-surat/${deliveryList.id}` : "/antar-surat")}
              className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="open-antar-surat-from-map-btn"
              title="Daftar antar surat tunggakan"
            >
              <FileText className="w-4 h-4" /> Antar Surat
            </Button>
            {(userRole === "petugas" || isAdmin) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(deliveryList && overlayMode === "tunggakan" ? `/tunggakan/${deliveryList.id}` : "/tunggakan")}
                className="gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="open-tunggakan-from-map-btn"
                title="Daftar tagihan menunggak"
              >
                <HandCoins className="w-4 h-4" /> Tunggakan
              </Button>
            )}
            <Button
              size="sm"
              variant={pickingCalon ? "default" : "outline"}
              onClick={() => { setPickingCalon(v => !v); setPickingForNew(false); setMovingId(null); }}
              className={pickingCalon ? "gap-1 bg-yellow-500 hover:bg-yellow-600 text-white" : "gap-1 border-yellow-400 text-yellow-700 hover:bg-yellow-50"}
              data-testid="toggle-calon-btn"
              title="Tambah calon pelanggan (titik kuning) dengan klik peta"
            >
              <MapPin className="w-4 h-4" /> {pickingCalon ? "Klik di peta..." : "Calon"}
            </Button>
            {(isAdmin || userRole === "petugas" || userRole === "kasir") && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkPhoto(true)}
                className="gap-1 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
                data-testid="open-bulk-photo-btn"
                title="Upload banyak foto sekaligus (nama file = ID pelanggan)"
              >
                <ImageIcon className="w-4 h-4" /> Foto Massal
              </Button>
            )}
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkSync(true)}
                className="gap-1 border-cyan-400 text-cyan-700 hover:bg-cyan-50"
                data-testid="open-bulk-sync-btn"
                title="Sinkron semua pelanggan dari PDAM Tebo Online"
              >
                🔄 Sinkron PDAM
              </Button>
            )}
          </div>
        </div>

        {/* Secondary toolbar row — meter-reader utilities placed BELOW the main row */}
        <div className="px-3 pb-2 flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant={showIdLabels ? "default" : "outline"}
            onClick={() => setShowIdLabels(v => !v)}
            className={showIdLabels
              ? "gap-1 bg-slate-800 hover:bg-slate-900 text-white"
              : "gap-1 border-slate-300 text-slate-700 hover:bg-slate-50"}
            data-testid="toggle-id-labels-btn"
            title="Tampilkan ID pelanggan di atas setiap titik (memudahkan baca meter)"
          >
            <span className="font-mono text-[11px] px-1">ID</span>
            {showIdLabels ? "Sembunyikan ID" : "Tampilkan ID"}
          </Button>
          <Button
            size="sm"
            variant={turboDots ? "default" : "outline"}
            onClick={() => setTurboDots(v => !v)}
            className={turboDots
              ? "gap-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              : "gap-1 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"}
            data-testid="toggle-turbo-btn"
            title="Mode Turbo: gambar semua titik di 1 canvas (lebih cepat di HP). Klik titik tetap bisa."
          >
            <ZoomIn className="w-3.5 h-3.5" />
            {turboDots ? "Turbo ON" : "Turbo OFF"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowExport(true)}
            className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="open-export-btn"
            title="Export peta sebagai JPG atau PDF"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </Button>
        </div>

        {/* Antar Surat overlay banner */}
        {antarSuratId && (
          <div className="px-3 py-2 bg-amber-50 border-y border-amber-200 flex items-center gap-2 flex-wrap" data-testid="antar-surat-banner">
            <FileText className="w-4 h-4 text-amber-700" />
            {loadingDelivery ? (
              <span className="text-xs text-amber-700">Memuat daftar antar surat...</span>
            ) : deliveryList ? (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-amber-800 truncate">
                    Mode Antar Surat: {deliveryList.name}
                  </div>
                  <div className="text-[10px] text-amber-700 flex gap-2 flex-wrap">
                    <span>🔴 Belum {deliveryList.counts.pending}</span>
                    <span>🟢 Sudah {deliveryList.counts.delivered}</span>
                    <span>🔵 Janji {deliveryList.counts.promised_pay || 0}</span>
                    <span>💰 Lunas {deliveryList.counts.lunas || 0}</span>
                    <span>✂️ Putus {deliveryList.counts.putus || 0}</span>
                    <span>⚫ Tidak Ketemu {deliveryList.counts.not_found}</span>
                    {monthRange.active && (
                      <span className="text-purple-700 font-semibold">
                        · Filter bulan {monthRange.min ?? 0}–{monthRange.max ?? "∞"}
                      </span>
                    )}
                    {asStatusFilter && (
                      <span className="text-emerald-700 font-semibold" data-testid="map-status-filter-indicator">
                        · Filter: {AS_STATUS_LABEL[asStatusFilter] || asStatusFilter}
                      </span>
                    )}
                    <span className="text-amber-600">· {effectiveCustomers.length} dari {deliveryList.counts.total} pin punya GPS</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={() => navigate(`/antar-surat/${deliveryList.id}`)}
                  data-testid="open-delivery-detail-btn"
                >
                  <Edit3 className="w-3 h-3" /> Detail
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1 text-xs text-rose-700 hover:bg-rose-50"
                  onClick={() => navigate("/peta-global")}
                  data-testid="exit-delivery-mode-btn"
                >
                  <X className="w-3 h-3" /> Keluar Mode
                </Button>
                <div className="w-full flex items-center gap-1.5 flex-wrap pt-1.5 mt-1 border-t border-amber-200" data-testid="map-status-filter-bar">
                  <span className="text-[10px] font-semibold text-amber-800 inline-flex items-center gap-1">
                    <Filter className="w-3 h-3" /> Filter Status:
                  </span>
                  {AS_STATUS_FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.value || "all"}
                      onClick={() => setAsStatusFilter(opt.value)}
                      className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                        asStatusFilter === opt.value
                          ? "bg-amber-600 text-white border-amber-600"
                          : "bg-white text-amber-800 border-amber-300 hover:bg-amber-100"
                      }`}
                      data-testid={`map-filter-${opt.value || "all"}-btn`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <span className="text-xs text-rose-700">Daftar antar surat tidak ditemukan.</span>
            )}
          </div>
        )}

        {/* Tunggakan overlay banner */}
        {tunggakanId && (
          <div className="px-3 py-2 bg-rose-50 border-y border-rose-200 flex items-center gap-2 flex-wrap" data-testid="tunggakan-banner">
            <HandCoins className="w-4 h-4 text-rose-700" />
            {loadingDelivery ? (
              <span className="text-xs text-rose-700">Memuat daftar tunggakan...</span>
            ) : (deliveryList && overlayMode === "tunggakan") ? (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-rose-800 truncate">
                    Mode Tunggakan: {deliveryList.name}
                  </div>
                  <div className="text-[10px] text-rose-700 flex gap-2 flex-wrap">
                    <span>🔴 Belum {deliveryList.counts.pending}</span>
                    <span>🟠 Ditagih {deliveryList.counts.ditagih}</span>
                    <span>🔵 Janji {deliveryList.counts.janji_bayar}</span>
                    <span>🟢 Lunas {deliveryList.counts.lunas}</span>
                    <span className="text-rose-600">· {effectiveCustomers.length} dari {deliveryList.counts.total} pin punya GPS</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 text-xs border-rose-300 text-rose-800 hover:bg-rose-100"
                  onClick={() => navigate(`/tunggakan/${deliveryList.id}`)}
                  data-testid="open-tunggakan-detail-btn"
                >
                  <Edit3 className="w-3 h-3" /> Detail
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1 text-xs text-rose-700 hover:bg-rose-100"
                  onClick={() => navigate("/peta-global")}
                  data-testid="exit-tunggakan-mode-btn"
                >
                  <X className="w-3 h-3" /> Keluar Mode
                </Button>
              </>
            ) : (
              <span className="text-xs text-rose-700">Daftar tunggakan tidak ditemukan.</span>
            )}
          </div>
        )}

        {/* Search + filter row */}
        <div className="px-3 pb-2 space-y-2">
          <form onSubmit={onSubmitSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cari ID, nama, atau alamat..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="search-btn">
              Cari
            </Button>
          </form>

          {showFilter && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <FilterSelect label="Wilayah" value={filter.wilayah} options={facets.wilayah}
                onChange={(v) => setFilter(f => ({ ...f, wilayah: v }))} testid="filter-wilayah" />
              <FilterSelect label="Rayon" value={filter.rayon} options={facets.rayon}
                onChange={(v) => setFilter(f => ({ ...f, rayon: v }))} testid="filter-rayon" />
              <FilterSelect label="Kecamatan" value={filter.kecamatan} options={facets.kecamatan}
                onChange={(v) => setFilter(f => ({ ...f, kecamatan: v }))} testid="filter-kecamatan" />
              <FilterSelect label="Status" value={filter.status} options={["Aktif", "Non Aktif"]}
                onChange={(v) => setFilter(f => ({ ...f, status: v }))} testid="filter-status" />
              <div className="col-span-2 sm:col-span-4 flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={clearFilters} data-testid="clear-filter-btn">Reset</Button>
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  onClick={() => { fetchCustomers(); setShowFilter(false); }}
                  data-testid="apply-filter-btn">
                  Terapkan
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mode banner */}
      {(pickingForNew || movingId) && (
        <div className="bg-amber-100 border-b border-amber-300 px-4 py-2 text-sm text-amber-900 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Crosshair className="w-4 h-4" />
            {pickingForNew ? "Klik di peta untuk menambahkan pelanggan baru." :
              `Klik di peta untuk memindahkan pin ${movingId}.`}
          </span>
          <Button size="sm" variant="ghost" onClick={() => { setPickingForNew(false); setMovingId(null); }}
            data-testid="cancel-mode-btn">
            <X className="w-3 h-3 mr-1" /> Batal
          </Button>
        </div>
      )}
      {pickingCalon && (
        <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 text-sm text-yellow-900 flex items-center justify-between" data-testid="calon-mode-banner">
          <span className="flex items-center gap-2">
            <Crosshair className="w-4 h-4" />
            Klik di peta untuk menandai lokasi calon pelanggan (titik kuning).
          </span>
          <Button size="sm" variant="ghost" onClick={() => setPickingCalon(false)} data-testid="cancel-calon-mode-btn">
            <X className="w-3 h-3 mr-1" /> Batal
          </Button>
        </div>
      )}

      {/* Map */}
      <div className="relative" style={{ height: "calc(100vh - 130px)" }}>
        {loading && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-600" /> Memuat pelanggan...
          </div>
        )}

        {/* Base layer toggle */}
        <div className="absolute top-3 right-3 z-[500] bg-white shadow rounded-lg p-1 flex gap-1">
          {Object.entries(baseLayers).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setBase(k)}
              className={`px-2 py-1 text-xs rounded ${base === k ? "bg-cyan-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              data-testid={`layer-${k}`}
            >
              <Layers className="w-3 h-3 inline mr-1" />{v.name}
            </button>
          ))}
        </div>

        <MapContainer
          center={savedViewRef.current ? savedViewRef.current.center : DEFAULT_CENTER}
          zoom={savedViewRef.current ? savedViewRef.current.zoom : DEFAULT_ZOOM}
          className="w-full h-full"
          zoomControl={false}
          preferCanvas={true}
          zoomAnimation={!IS_MOBILE}
          fadeAnimation={!IS_MOBILE}
          markerZoomAnimation={!IS_MOBILE}
          wheelDebounceTime={IS_MOBILE ? 200 : 40}
          wheelPxPerZoomLevel={IS_MOBILE ? 120 : 60}
        >
          <TileLayer
            key={base}
            url={baseLayers[base].urls[0]}
            attribution={baseLayers[base].attribution}
            maxZoom={baseLayers[base].maxZoom}
            maxNativeZoom={baseLayers[base].maxZoom}
            subdomains={baseLayers[base].subdomains}
            updateWhenIdle={false}
            updateWhenZooming={false}
            keepBuffer={2}
          />
          <ClickHandler onClick={onMapClick} />
          <ViewportTracker onChange={onViewportChange} />
          <MapRefCapture onReady={onMapReady} />
          <FlyTo target={flyTarget} />

          {/* Cluster bubbles (when zoomed out) — single Marker w/ divIcon (cheap) */}
          {visibleItems.clusters.map((cl) => {
            const [lng, lat] = cl.geometry.coordinates;
            const count = cl.properties.point_count;
            return (
              <Marker
                key={`cl-${cl.id}`}
                position={[lat, lng]}
                icon={getClusterIcon(count)}
                eventHandlers={{
                  click: () => {
                    if (!mapRef.current) return;
                    const expansionZoom = Math.min(
                      clusterIndex.getClusterExpansionZoom(cl.id),
                      20
                    );
                    mapRef.current.flyTo([lat, lng], expansionZoom, { duration: 0.5 });
                  },
                }}
              />
            );
          })}

          {/* Individual customer pins — NO per-marker popup (shared popup below).
              On mobile we render a SINGLE CircleMarker per pin (finger-sized) to halve
              the number of Leaflet paths & React nodes on the map. On desktop we keep
              the visible-pin + invisible-tap-target pair for pixel-perfect tiny markers.
              When Turbo Dot mode is ON, dots are drawn by <TurboDotsLayer/> below and
              we skip mounting per-point React nodes entirely (huge perf win on phones). */}
          {!turboDots && visibleItems.points.map((pt) => {
            const cid = pt.properties.cid;
            const c = customerById.get(cid);
            if (!c) return null;
            if (movingId === cid) {
              return (
                <Marker
                  key={cid}
                  position={[c.latitude, c.longitude]}
                  icon={editIcon}
                />
              );
            }
            // In Antar Surat overlay: color marker by delivery status
            const deliveryItem = deliveryItemMap.get(cid);
            const color = deliveryItem
              ? (OVERLAY_STATUS_COLORS[deliveryItem.status] || "#ef4444")
              : statusColor(c.status);
            const markerRadiusOverride = deliveryItem ? Math.max(markerRadius, 7) : markerRadius;
            const handleSelect = (e) => {
              if (e && e.originalEvent) {
                L.DomEvent.stopPropagation(e.originalEvent);
              }
              setSelectedPin(c);
            };

            // MOBILE / TOUCH: single fat CircleMarker that is both visible + tap target.
            if (IS_MOBILE) {
              return (
                <CircleMarker
                  key={cid}
                  center={[c.latitude, c.longitude]}
                  radius={markerRadiusOverride}
                  bubblingMouseEvents={false}
                  pathOptions={{
                    color: "#ffffff",
                    weight: deliveryItem ? 2 : 1.2,
                    fillColor: color,
                    fillOpacity: 0.95,
                  }}
                  eventHandlers={{ click: handleSelect }}
                />
              );
            }

            // DESKTOP: keep visible small pin + invisible enlarged hit target.
            const hitRadius = Math.max(markerRadiusOverride, MOUSE_HIT_RADIUS);
            return (
              <React.Fragment key={cid}>
                <CircleMarker
                  center={[c.latitude, c.longitude]}
                  radius={markerRadiusOverride}
                  interactive={false}
                  pathOptions={{
                    color: "#ffffff",
                    weight: deliveryItem ? 2 : 1.2,
                    fillColor: color,
                    fillOpacity: 0.95,
                  }}
                />
                <CircleMarker
                  center={[c.latitude, c.longitude]}
                  radius={hitRadius}
                  bubblingMouseEvents={false}
                  pathOptions={{
                    stroke: false,
                    fillColor: "#000000",
                    fillOpacity: 0.001,
                  }}
                  eventHandlers={{ click: handleSelect }}
                />
              </React.Fragment>
            );
          })}

          {/* Turbo Dot mode — draws every visible point on a single canvas.
              Skipped when in picking/moving mode so the map's raw click still fires. */}
          {turboDots && (
            <TurboDotsLayer
              points={turboPoints}
              onSelect={onTurboSelect}
              enabled={turboEnabled}
            />
          )}

          {/* Amber edit icon for the pin currently being moved (both Turbo & normal). */}
          {turboDots && movingId && (() => {
            const c = customerById.get(movingId);
            if (!c || c.latitude == null || c.longitude == null) return null;
            return (
              <Marker
                key={`moving-${movingId}`}
                position={[c.latitude, c.longitude]}
                icon={editIcon}
              />
            );
          })()}

          {/* Permanent ID labels above each individual pin (meter-reader helper).
              Only rendered at close zoom to avoid clutter/perf issues. */}
          {showIdLabels && viewport.zoom >= 17 && visibleItems.points.map((pt) => {
            const cid = pt.properties.cid;
            const c = customerById.get(cid);
            if (!c) return null;
            if (movingId === cid) return null;
            return (
              <Marker
                key={`lbl-${cid}`}
                position={[c.latitude, c.longitude]}
                icon={getIdLabelIcon(cid)}
                interactive={false}
                keyboard={false}
              />
            );
          })}

          {/* Shared popup — mounted ONLY for the currently selected pin */}
          {selectedPin && (() => {
            const c = customerById.get(selectedPin.customer_id) || selectedPin;
            const deliveryItem = deliveryItemMap.get(c.customer_id);
            return (
              <Marker
                key={`sel-${c.customer_id}`}
                position={[c.latitude, c.longitude]}
                icon={popupAnchorIcon}
                opacity={0}
                interactive={false}
                eventHandlers={{
                  add: (e) => e.target.openPopup(),
                  popupclose: () => setSelectedPin(null),
                }}
              >
                <Popup minWidth={300} maxWidth={340}>
                  <div className="text-[15px] space-y-1.5">
                    <div className="font-bold text-slate-900 text-lg leading-tight">{c.nama || "(Tanpa nama)"}</div>
                    <div className="font-mono text-xs text-slate-500">ID: {c.customer_id}</div>
                    <div className="text-sm"><b>Alamat:</b> {c.alamat || "-"}</div>
                    {deliveryItem && overlayMode === "tunggakan" && (
                      <div className="bg-rose-50 border border-rose-200 rounded p-2 my-1 text-xs">
                        <div className="font-semibold text-rose-800 flex items-center gap-1 mb-0.5">
                          <HandCoins className="w-3 h-3" /> Tunggakan: {deliveryItem.jml_bulan || 0} bulan
                        </div>
                        <div>
                          {deliveryItem.status === "lunas" && <span className="text-emerald-700 font-bold">🟢 Lunas</span>}
                          {deliveryItem.status === "ditagih" && <span className="text-amber-700 font-bold">🟠 Sudah Ditagih</span>}
                          {deliveryItem.status === "tidak_ketemu" && <span className="text-slate-600 font-bold">⚫ Tidak Ketemu</span>}
                          {deliveryItem.status === "janji_bayar" && (
                            <span className="text-sky-700 font-bold">
                              🔵 Janji Bayar{deliveryItem.promised_date ? ` (${deliveryItem.promised_date})` : ""}
                            </span>
                          )}
                          {(!deliveryItem.status || deliveryItem.status === "pending") && <span className="text-rose-700 font-bold">🔴 Belum Ditagih</span>}
                        </div>
                        {deliveryItem.note && <div className="text-[10px] text-rose-700 italic mt-0.5">📝 {deliveryItem.note}</div>}
                        <button
                          onClick={() => { setSelectedPin(null); navigate(`/tunggakan/${deliveryList.id}`); }}
                          className="mt-1 text-[11px] text-rose-700 hover:underline font-semibold"
                          data-testid={`open-tunggakan-item-${c.customer_id}-btn`}
                        >
                          ✏️ Buka Detail untuk Ubah Status
                        </button>
                      </div>
                    )}
                    {deliveryItem && overlayMode !== "tunggakan" && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-2 my-1 text-xs">
                        <div className="font-semibold text-amber-800 flex items-center gap-1 mb-0.5">
                          <FileText className="w-3 h-3" /> Status Antar Surat:
                        </div>
                        <div>
                          {deliveryItem.status === "delivered" && <span className="text-emerald-700 font-bold">🟢 Sudah Antar</span>}
                          {deliveryItem.status === "not_found" && <span className="text-slate-600 font-bold">⚫ Tidak Ketemu</span>}
                          {deliveryItem.status === "lunas" && <span className="text-teal-700 font-bold">💰 Lunas</span>}
                          {deliveryItem.status === "putus" && <span className="text-purple-700 font-bold">✂️ Putus</span>}
                          {deliveryItem.status === "promised_pay" && (
                            <span className="text-sky-700 font-bold">
                              🔵 Janji Bayar{deliveryItem.promised_date ? ` (${deliveryItem.promised_date})` : ""}
                            </span>
                          )}
                          {(!deliveryItem.status || deliveryItem.status === "pending") && <span className="text-rose-700 font-bold">🔴 Belum Antar</span>}
                        </div>
                        {DATED_STATUSES.includes(deliveryItem.status) && deliveryItem.status_date && (
                          <div className="mt-0.5 text-[11px] text-slate-600 inline-flex items-center gap-1" data-testid={`popup-status-date-${c.customer_id}`}>
                            <Calendar className="w-3 h-3" /> Tanggal: {deliveryItem.status_date}
                          </div>
                        )}
                        {typeof deliveryItem.jumlah_tagihan === "number" && (
                          <div className="mt-0.5" data-testid={`popup-jumlah-bulan-${c.customer_id}`}>
                            {deliveryItem.jumlah_tagihan === 0 ? (
                              <span className="text-teal-700 font-semibold">💰 Lunas (0 bulan)</span>
                            ) : (
                              <span className="text-rose-700 font-semibold">
                                🔴 {deliveryItem.jumlah_tagihan} bulan · Rp {Number(deliveryItem.total_unpaid || 0).toLocaleString("id-ID")}
                              </span>
                            )}
                          </div>
                        )}
                        {deliveryItem.note && <div className="text-[10px] text-amber-700 italic mt-0.5">📝 {deliveryItem.note}</div>}
                        <button
                          onClick={() => { setSelectedPin(null); setEditDeliveryItem(deliveryItem); }}
                          className="mt-1 text-[11px] text-amber-700 hover:underline font-semibold"
                          data-testid={`edit-delivery-${c.customer_id}-btn`}
                        >
                          ✏️ Ubah Status / Tambah Foto Bukti
                        </button>
                      </div>
                    )}
                    <div className="text-sm flex flex-wrap gap-1 mt-1">
                      {c.wilayah && <span className="px-2 py-0.5 rounded bg-cyan-50 text-cyan-700 text-xs">W: {c.wilayah}</span>}
                      {c.rayon && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">R: {c.rayon}</span>}
                      {c.golongan && <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs">{c.golongan}</span>}
                      <span className={`px-2 py-0.5 rounded text-xs ${String(c.status).toLowerCase().includes("non") ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {c.status || "Aktif"}
                      </span>
                    </div>
                    {c.nomor_telp && (
                      <div className="text-sm">📞 {c.nomor_telp}</div>
                    )}
                    {/* Inline bill check result ("Cek") */}
                    {popupBill[c.customer_id] && (
                      <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-2.5 text-sm mt-1" data-testid={`bill-check-result-${c.customer_id}`}>
                        {popupBill[c.customer_id].loading ? (
                          <div className="flex items-center gap-2 text-cyan-700 font-medium">
                            <Loader2 className="w-4 h-4 animate-spin" /> Mengecek tagihan...
                          </div>
                        ) : popupBill[c.customer_id].error ? (
                          <div className="text-rose-700 font-medium">⚠️ {popupBill[c.customer_id].error}</div>
                        ) : popupBill[c.customer_id].count === 0 ? (
                          <div className="text-emerald-700 font-semibold">💰 Lunas — tidak ada tagihan</div>
                        ) : (
                          <div className="text-rose-700 font-semibold">
                            🔴 {popupBill[c.customer_id].count} bulan · Rp {Number(popupBill[c.customer_id].total || 0).toLocaleString("id-ID")}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex gap-1.5 pt-2 border-t border-slate-100 mt-2">
                      {!canEdit ? (
                        <button onClick={() => { setSelectedPin(null); openEdit(c); }}
                          className="flex-1 px-2 py-1.5 text-sm bg-slate-50 text-slate-700 rounded hover:bg-slate-100 flex items-center justify-center gap-1"
                          data-testid={`view-btn-${c.customer_id}`}>
                          👁️ Lihat Detail
                        </button>
                      ) : (
                        <>
                          <button onClick={() => { setSelectedPin(null); openEdit(c); }}
                            className="flex-1 px-2 py-1.5 text-sm bg-cyan-50 text-cyan-700 rounded hover:bg-cyan-100 flex items-center justify-center gap-1"
                            data-testid={`edit-btn-${c.customer_id}`}>
                            <Edit3 className="w-4 h-4" /> Edit
                          </button>
                          <button onClick={() => { setSelectedPin(null); setMovingId(c.customer_id); setPickingForNew(false); }}
                            className="flex-1 px-2 py-1.5 text-sm bg-amber-50 text-amber-700 rounded hover:bg-amber-100 flex items-center justify-center gap-1"
                            data-testid={`move-btn-${c.customer_id}`}>
                            <Navigation className="w-4 h-4" /> Pindah
                          </button>
                          <button onClick={() => { setSelectedPin(null); deleteCustomer(c.customer_id); }}
                            className="px-2.5 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100"
                            title="Hapus titik"
                            data-testid={`delete-btn-${c.customer_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                    {!isReadOnly && (
                      <div className="flex gap-1.5 mt-1.5">
                        <button onClick={() => checkBillInline(c.customer_id)}
                          disabled={popupBill[c.customer_id]?.loading}
                          className="flex-1 px-2 py-2 text-sm font-semibold bg-cyan-600 text-white rounded hover:bg-cyan-700 disabled:opacity-60 flex items-center justify-center gap-1"
                          data-testid={`check-btn-${c.customer_id}`}>
                          {popupBill[c.customer_id]?.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Cek
                        </button>
                        <button onClick={() => { setSelectedPin(null); goToPayment(c.customer_id); }}
                          className="flex-1 px-2 py-2 text-sm font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center justify-center gap-1"
                          data-testid={`pay-btn-${c.customer_id}`}>
                          <CreditCard className="w-4 h-4" /> Bayar
                        </button>
                      </div>
                    )}
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${c.latitude},${c.longitude}`}
                      target="_blank" rel="noopener noreferrer"
                      className="block text-center text-sm text-blue-600 hover:underline pt-1.5"
                    >
                      🧭 Navigasi Google Maps
                    </a>
                  </div>
                </Popup>
              </Marker>
            );
          })()}

          {/* Calon pelanggan (prospective) — yellow pins */}
          {calonList.map((cp) => {
            if (cp.latitude == null || cp.longitude == null) return null;
            return (
              <CircleMarker
                key={`calon-${cp.id}`}
                center={[cp.latitude, cp.longitude]}
                radius={Math.max(markerRadius, 7)}
                bubblingMouseEvents={false}
                pathOptions={{ color: "#fff", weight: 2, fillColor: "#eab308", fillOpacity: 0.95 }}
                eventHandlers={{
                  click: (e) => {
                    if (e?.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
                    setSelectedCalon(cp);
                  },
                }}
              />
            );
          })}

          {/* Calon popup */}
          {selectedCalon && (
            <Marker
              key={`calon-sel-${selectedCalon.id}`}
              position={[selectedCalon.latitude, selectedCalon.longitude]}
              icon={popupAnchorIcon}
              opacity={0}
              interactive={false}
              eventHandlers={{
                add: (e) => e.target.openPopup(),
                popupclose: () => setSelectedCalon(null),
              }}
            >
              <Popup minWidth={220}>
                <div className="text-sm space-y-1" data-testid="calon-popup">
                  <div className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">
                    <MapPin className="w-3 h-3" /> CALON PELANGGAN
                  </div>
                  <div className="font-bold text-slate-800">{selectedCalon.nama || "(Tanpa nama)"}</div>
                  {selectedCalon.customer_id && (
                    <div className="font-mono text-[11px] text-slate-500">ID: {selectedCalon.customer_id}</div>
                  )}
                  {selectedCalon.alamat && <div className="text-xs"><b>Alamat:</b> {selectedCalon.alamat}</div>}
                  {selectedCalon.nomor_telp && <div className="text-xs">📞 {selectedCalon.nomor_telp}</div>}
                  {selectedCalon.note && <div className="text-[11px] text-slate-600 italic">📝 {selectedCalon.note}</div>}
                  {selectedCalon.photo && (
                    <img src={selectedCalon.photo} alt="calon" className="w-full rounded border border-slate-200 mt-1" />
                  )}
                  <div className="flex gap-1 pt-2 border-t border-slate-100 mt-2">
                    <button
                      onClick={() => { setCalonModal({ mode: "edit", data: { ...selectedCalon, _photo_new: "" } }); setSelectedCalon(null); }}
                      className="flex-1 px-2 py-1 text-xs bg-cyan-50 text-cyan-700 rounded hover:bg-cyan-100 flex items-center justify-center gap-1"
                      data-testid={`calon-edit-btn-${selectedCalon.id}`}>
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => deleteCalon(selectedCalon.id)}
                      className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                      data-testid={`calon-delete-btn-${selectedCalon.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <button
                    onClick={() => { setCalonModal({ mode: "edit", data: { ...selectedCalon, status: "aktif", _photo_new: "" } }); setSelectedCalon(null); }}
                    className="w-full mt-1 px-2 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center justify-center gap-1"
                    data-testid={`calon-activate-btn-${selectedCalon.id}`}>
                    ✅ Jadikan Aktif (gabung ke master)
                  </button>
                </div>
              </Popup>
            </Marker>
          )}

          {/* === Real-time GPS — "you are here" blue dot + accuracy halo === */}
          {gpsPos && (
            <>
              {gpsPos.accuracy > 0 && (
                <LeafletCircle
                  center={[gpsPos.lat, gpsPos.lng]}
                  radius={gpsPos.accuracy}
                  interactive={false}
                  pathOptions={{
                    color: "#1a73e8",
                    weight: 1,
                    fillColor: "#1a73e8",
                    fillOpacity: 0.12,
                  }}
                />
              )}
              <Marker
                position={[gpsPos.lat, gpsPos.lng]}
                icon={makeGpsIcon(gpsPos.heading)}
                interactive={false}
                keyboard={false}
                zIndexOffset={1000}
              />
            </>
          )}
        </MapContainer>

        {/* Kontrol peta: zoom +/- dan tombol lokasi, ditumpuk di kanan-bawah */}
        <div className="absolute z-[500] left-3 top-3 flex flex-col items-center gap-2">
          {/* Zoom in / out */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col">
            <button
              onClick={() => mapRef.current && mapRef.current.zoomIn()}
              aria-label="Perbesar"
              className="w-12 h-11 flex items-center justify-center text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors border-b border-slate-200"
              data-testid="map-zoom-in-btn"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button
              onClick={() => mapRef.current && mapRef.current.zoomOut()}
              aria-label="Perkecil"
              className="w-12 h-11 flex items-center justify-center text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
              data-testid="map-zoom-out-btn"
            >
              <Minus className="w-5 h-5" />
            </button>
          </div>
          {/* Tombol Lokasi Saya (di bawah tombol +/-) */}
          <button
            onClick={toggleGps}
            aria-label="Lokasi saya"
            className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all
              ${gpsMode === "follow" ? "bg-blue-600 text-white"
                : gpsMode === "on" ? "bg-white text-blue-600 ring-2 ring-blue-500"
                : "bg-white text-slate-600"}`}
            data-testid="gps-toggle-btn"
          >
            {gpsMode === "follow" ? (
              <LocateFixed className="w-5 h-5" />
            ) : (
              <Locate className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Modal Add/Edit */}
      {modalMode && modalData && (
        <CustomerModal
          mode={modalMode}
          data={modalData}
          onChange={setModalData}
          onClose={closeModal}
          onSave={saveModal}
          saving={saving}
          authHeaders={auth}
          isAdmin={isAdmin}
          isReadOnly={isReadOnly}
          onGenerateCode={generateUpdateCode}
          onReload={fetchCustomers}
        />
      )}

      {/* Bulk Sync from PDAM Online (admin) */}
      {showBulkSync && (
        <BulkSyncModal
          authHeaders={auth}
          facets={facets}
          onClose={() => setShowBulkSync(false)}
          onDone={() => { fetchCustomers(); }}
        />
      )}

      {/* Bulk Photo Upload (match by filename = customer ID) */}
      {showBulkPhoto && (
        <BulkPhotoModal
          authHeaders={auth}
          isAdmin={isAdmin}
          onClose={() => setShowBulkPhoto(false)}
          onDone={() => { fetchCustomers(); }}
        />
      )}

      {/* Add/Edit Calon Pelanggan */}
      {calonModal && (
        <CalonModal
          mode={calonModal.mode}
          data={calonModal.data}
          onChange={(d) => setCalonModal((prev) => prev ? { ...prev, data: d } : prev)}
          onClose={() => setCalonModal(null)}
          onSave={saveCalon}
        />
      )}

      {/* Inline Antar Surat status editor (opened directly from a map pin) */}
      {editDeliveryItem && deliveryList && (
        <DeliveryEditModal
          item={editDeliveryItem}
          listId={deliveryList.id}
          authToken={token}
          onClose={() => setEditDeliveryItem(null)}
          onUpdated={(updatedItem, newCounts) => {
            handleDeliveryUpdated(updatedItem, newCounts);
            setEditDeliveryItem(null);
          }}
        />
      )}

      {/* Export peta ke JPG / PDF */}
      {showExport && (
        <ExportMapModal
          mapRef={mapRef}
          base={base}
          showIdLabels={showIdLabels}
          allPoints={effectiveCustomers
            .filter((c) => c.latitude != null && c.longitude != null)
            .map((c) => {
              const di = deliveryItemMap.get(c.customer_id);
              return {
                cid: c.customer_id,
                lat: Number(c.latitude),
                lng: Number(c.longitude),
                color: di ? (OVERLAY_STATUS_COLORS[di.status] || "#ef4444") : statusColor(c.status),
              };
            })}
          viewportPoints={visibleItems.points
            .map((pt) => {
              const cid = pt.properties.cid;
              const c = customerById.get(cid);
              if (!c || c.latitude == null || c.longitude == null) return null;
              const di = deliveryItemMap.get(cid);
              return {
                cid,
                lat: Number(c.latitude),
                lng: Number(c.longitude),
                color: di ? (OVERLAY_STATUS_COLORS[di.status] || "#ef4444") : statusColor(c.status),
              };
            })
            .filter(Boolean)}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Offline download (pins + map tiles) */}
      {showOfflineDl && (
        <OfflineDownloadModal
          mapRef={mapRef}
          customers={overlayMode === "antar_surat" ? effectiveCustomers : customers}
          scopedBounds={overlayMode === "antar_surat" ? antarBounds : null}
          scopedLabel={overlayMode === "antar_surat" ? (deliveryList?.name || "Antar Surat") : null}
          base={base}
          baseLayer={baseLayers[base]}
          offlineMeta={offlineMeta}
          onSavedMeta={setOfflineMeta}
          onClose={() => setShowOfflineDl(false)}
        />
      )}

      {/* Persistent dialog: Generated Update Code (Admin only) */}
      {generatedCode && (
        <div
          className="fixed inset-0 z-[2000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="generated-code-dialog"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <KeyRound className="w-5 h-5" />
                <h3 className="font-semibold">Kode Update Berhasil Dibuat</h3>
              </div>
              <button
                onClick={() => setGeneratedCode(null)}
                className="text-white/80 hover:text-white"
                data-testid="close-code-dialog-btn"
                aria-label="Tutup"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-slate-600">
                Pelanggan: <span className="font-medium text-slate-900">{generatedCode.customer_id}</span>
                <span className="text-slate-400"> — </span>
                <span className="text-slate-700">{generatedCode.nama}</span>
              </div>

              <div className="bg-slate-50 border-2 border-dashed border-purple-300 rounded-xl p-5 text-center">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Kode 15 Menit</div>
                <div
                  className="text-4xl font-mono font-bold tracking-[0.3em] text-purple-700 select-all"
                  data-testid="generated-code-value"
                >
                  {generatedCode.code}
                </div>
                {generatedCode.expires_at && (
                  <div className="text-xs text-slate-500 mt-3">
                    Berlaku sampai:{" "}
                    <span className="font-medium text-slate-700">
                      {new Date(generatedCode.expires_at).toLocaleString("id-ID")}
                    </span>
                  </div>
                )}
              </div>

              <Button
                onClick={copyGeneratedCode}
                className={`w-full ${codeCopied ? "bg-emerald-600 hover:bg-emerald-700" : "bg-purple-600 hover:bg-purple-700"} text-white`}
                data-testid="copy-code-btn"
              >
                {codeCopied ? (
                  <><Check className="w-4 h-4 mr-2" /> Tersalin</>
                ) : (
                  <><Copy className="w-4 h-4 mr-2" /> Salin Kode</>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center leading-relaxed">
                Berikan kode ini kepada petugas. Kode hanya bisa digunakan <b>1x</b> untuk membuka kunci edit pin/foto pelanggan ini.
              </p>

              <button
                onClick={() => setGeneratedCode(null)}
                className="w-full py-2 text-sm text-slate-600 hover:text-slate-900"
                data-testid="dismiss-code-dialog-btn"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compress an image File to a JPEG base64 data URL (max side 1280, ~<1MB).
function compressImageFile(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxSide = 1280;
      let w = img.width, h = img.height;
      if (w > h && w > maxSide) { h = Math.round(h * maxSide / w); w = maxSide; }
      else if (h > maxSide) { w = Math.round(w * maxSide / h); h = maxSide; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      let dataUrl = canvas.toDataURL("image/jpeg", 0.78);
      if (dataUrl.length > 1_500_000) dataUrl = canvas.toDataURL("image/jpeg", 0.6);
      resolve(dataUrl);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Gagal membaca foto.")); };
    img.src = url;
  });
}

// Modal: add/edit a Calon Pelanggan (prospective customer, yellow pin)
function CalonModal({ mode, data, onChange, onClose, onSave }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const fileRef = useRef(null);
  const [busyPhoto, setBusyPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const photoSrc = data._photo_new || data.photo || "";

  const onPickPhoto = async (file) => {
    if (!file) return;
    setBusyPhoto(true);
    try {
      const dataUrl = await compressImageFile(file);
      set("_photo_new", dataUrl);
    } catch (e) {
      toast.error("Gagal memuat foto.");
    } finally {
      setBusyPhoto(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-3" data-testid="calon-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-yellow-600" />
            {mode === "add" ? "Tambah Calon Pelanggan" : "Edit Calon Pelanggan"}
          </h3>
          <button onClick={onClose} data-testid="calon-close-btn"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Nama (opsional)">
            <Input value={data.nama || ""} onChange={(e) => set("nama", e.target.value)} data-testid="calon-nama-input" placeholder="Nama calon pelanggan" />
          </Field>
          <Field label="ID Pelanggan (opsional, wajib jika Aktif)">
            <Input value={data.customer_id || ""} onChange={(e) => set("customer_id", e.target.value)} className="font-mono" data-testid="calon-cid-input" placeholder="No. Sambungan" />
          </Field>
          <Field label="Alamat (opsional)">
            <Input value={data.alamat || ""} onChange={(e) => set("alamat", e.target.value)} data-testid="calon-alamat-input" placeholder="Alamat" />
          </Field>
          <Field label="No. HP (opsional)">
            <Input value={data.nomor_telp || ""} onChange={(e) => set("nomor_telp", e.target.value)} data-testid="calon-telp-input" placeholder="08xxxx" />
          </Field>
          <Field label="Catatan (opsional)">
            <Textarea value={data.note || ""} onChange={(e) => set("note", e.target.value)} rows={2} data-testid="calon-note-input" placeholder="Catatan survei..." />
          </Field>
          <Field label="Status">
            <select value={data.status || "calon"} onChange={(e) => set("status", e.target.value)}
              className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white" data-testid="calon-status-select">
              <option value="calon">Calon (titik kuning)</option>
              <option value="aktif">Aktif (gabung ke master)</option>
            </select>
          </Field>
          {(data.status || "calon") === "aktif" && (
            <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
              ✅ Saat disimpan, calon ini akan digabung ke master pelanggan. ID wajib diisi & tidak boleh dobel dengan pelanggan aktif.
            </div>
          )}
          <Field label="Foto (opsional)">
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => onPickPhoto(e.target.files?.[0])} data-testid="calon-photo-input" />
              <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busyPhoto} className="gap-1" data-testid="calon-photo-btn">
                {busyPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Pilih Foto
              </Button>
              {photoSrc && <img src={photoSrc} alt="preview" className="h-14 w-14 object-cover rounded border border-slate-200" />}
            </div>
          </Field>
          <div className="text-[11px] text-slate-500">📍 Lokasi: {Number(data.latitude).toFixed(5)}, {Number(data.longitude).toFixed(5)}</div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid="calon-cancel-btn">Batal</Button>
          <Button className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white" onClick={handleSave} disabled={saving} data-testid="calon-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Simpan
          </Button>
        </div>
      </div>
    </div>
  );
}

// Modal: bulk-upload house photos matched to customers by filename (= customer ID)
function BulkPhotoModal({ authHeaders, isAdmin, onClose, onDone }) {
  const fileRef = useRef(null);
  const [items, setItems] = useState([]); // {key, cid, photo, name, exists, has_photo, state, msg}
  const [preparing, setPreparing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const parseCid = (filename) => (filename || "").replace(/\.[^/.]+$/, "").trim();

  const onPickFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPreparing(true);
    try {
      const built = [];
      for (const f of files) {
        const cid = parseCid(f.name);
        let photo = "";
        try { photo = await compressImageFile(f); } catch { /* skip */ }
        built.push({ key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2)}`, cid, photo, name: "", exists: false, has_photo: false, state: photo && cid ? "checking" : "error", msg: !cid ? "Nama file tanpa ID" : (!photo ? "Gagal baca foto" : "") });
      }
      // Bulk check which IDs already have a photo
      const cids = built.filter(b => b.cid && b.photo).map(b => b.cid);
      let checkRes = {};
      if (cids.length) {
        try {
          const res = await axios.post(`${API}/peta-global/photos/check`, { customer_ids: cids }, authHeaders);
          checkRes = res.data?.results || {};
        } catch { /* ignore */ }
      }
      for (const b of built) {
        if (b.state === "error") continue;
        const info = checkRes[b.cid] || {};
        b.name = info.nama || "";
        b.exists = !!info.exists;
        b.has_photo = !!info.has_photo;
        b.state = info.has_photo ? "confirm" : "ready";
      }
      setItems(built);
    } finally {
      setPreparing(false);
    }
  };

  const uploadOne = async (item) => {
    try {
      await axios.post(`${API}/peta-global/customer/${encodeURIComponent(item.cid)}/photo`, { photo: item.photo }, authHeaders);
      return { ok: true };
    } catch (err) {
      return { ok: false, msg: err?.response?.data?.detail || "Gagal upload" };
    }
  };

  const setItemState = (key, patch) => setItems((prev) => prev.map((it) => it.key === key ? { ...it, ...patch } : it));

  const uploadReady = async () => {
    setUploading(true);
    let anyDone = false;
    const targets = items.filter((it) => it.state === "ready");
    for (const it of targets) {
      setItemState(it.key, { state: "uploading" });
      const r = await uploadOne(it);
      if (r.ok) { anyDone = true; setItemState(it.key, { state: "done", msg: "" }); }
      else setItemState(it.key, { state: "failed", msg: r.msg });
    }
    setUploading(false);
    if (anyDone && onDone) onDone();
  };

  const overwriteOne = async (it) => {
    setItemState(it.key, { state: "uploading" });
    const r = await uploadOne(it);
    if (r.ok) { setItemState(it.key, { state: "done", msg: "" }); if (onDone) onDone(); }
    else setItemState(it.key, { state: "failed", msg: r.msg });
  };

  const readyCount = items.filter((it) => it.state === "ready").length;
  const confirmCount = items.filter((it) => it.state === "confirm").length;
  const doneCount = items.filter((it) => it.state === "done").length;

  const badge = (it) => {
    const map = {
      checking: ["Cek...", "bg-slate-100 text-slate-600"],
      ready: ["Siap", "bg-sky-100 text-sky-700"],
      confirm: ["Sudah ada foto", "bg-amber-100 text-amber-700"],
      uploading: ["Upload...", "bg-blue-100 text-blue-700"],
      done: ["✓ Selesai", "bg-emerald-100 text-emerald-700"],
      failed: ["Gagal", "bg-red-100 text-red-700"],
      error: ["Error", "bg-red-100 text-red-700"],
      skipped: ["Dilewati", "bg-slate-100 text-slate-500"],
    };
    const [txt, cls] = map[it.state] || ["-", "bg-slate-100"];
    return <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{txt}</span>;
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-3" data-testid="bulk-photo-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-fuchsia-600" /> Upload Foto Massal
          </h3>
          <button onClick={onClose} data-testid="bulk-photo-close-btn"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2.5">
            Pilih banyak foto dari galeri. <b>Nama file harus = ID pelanggan</b> (mis. <span className="font-mono">12345.jpg</span> → foto pelanggan ID 12345). Foto yang pelanggannya sudah punya foto akan minta konfirmasi timpa.
            {!isAdmin && <div className="mt-1 text-amber-700">⚠️ Sebagai petugas, menimpa foto yang sudah ada butuh kode admin (akan gagal jika tanpa kode).</div>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => onPickFiles(e.target.files)} data-testid="bulk-photo-file-input" />
          <Button type="button" variant="outline" className="w-full gap-2" onClick={() => fileRef.current?.click()} disabled={preparing || uploading} data-testid="bulk-photo-pick-btn">
            {preparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Pilih Foto dari Galeri
          </Button>

          {items.length > 0 && (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span>{items.length} foto</span>
                <span className="text-sky-700">{readyCount} siap</span>
                <span className="text-amber-700">{confirmCount} perlu konfirmasi</span>
                <span className="text-emerald-700">{doneCount} selesai</span>
              </div>
              <div className="max-h-[40vh] overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded">
                {items.map((it) => (
                  <div key={it.key} className="flex items-center gap-2 p-2" data-testid={`bulk-photo-row-${it.cid || "noid"}`}>
                    {it.photo ? <img src={it.photo} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-400" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-slate-800 truncate">{it.cid || "(tanpa ID)"}</div>
                      <div className="text-[10px] text-slate-500 truncate">{it.name || (it.msg || "")}</div>
                    </div>
                    {badge(it)}
                    {it.state === "confirm" && (
                      <div className="flex gap-1">
                        <button onClick={() => overwriteOne(it)} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white" data-testid={`bulk-photo-overwrite-${it.cid}`}>Timpa</button>
                        <button onClick={() => setItemState(it.key, { state: "skipped" })} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" data-testid={`bulk-photo-skip-${it.cid}`}>Lewati</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid="bulk-photo-cancel-btn">Tutup</Button>
          <Button className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={uploadReady} disabled={uploading || readyCount === 0} data-testid="bulk-photo-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Upload {readyCount > 0 ? `(${readyCount})` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-600 mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
        data-testid={testid}>
        <option value="">Semua</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function CustomerModal({ mode, data, onChange, onClose, onSave, saving, authHeaders, isAdmin, isReadOnly, onGenerateCode, onReload }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const [inquiring, setInquiring] = useState(false);
  const [inquiryInfo, setInquiryInfo] = useState(null);
  const [inquiryError, setInquiryError] = useState("");
  const lastInquiredRef = useRef("");
  const debounceRef = useRef(null);
  const photoCamRef = useRef(null);
  const photoGalRef = useRef(null);

  // === Sync from PDAM Online state ===
  const [syncing, setSyncing] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null); // {changes, in_sync, current, pdam_data}
  const [syncError, setSyncError] = useState("");
  const [applyingSync, setApplyingSync] = useState(false);
  const [showPhotoZoom, setShowPhotoZoom] = useState(false);

  const runSyncPreview = async () => {
    if (!data.customer_id) return;
    setSyncing(true);
    setSyncError("");
    setSyncPreview(null);
    try {
      const url = `${API}/peta-global/customer/${encodeURIComponent(data.customer_id)}/sync-preview`;
      const res = await axios.get(url, authHeaders);
      setSyncPreview(res.data);
    } catch (err) {
      setSyncError(err?.response?.data?.detail || "Gagal mengambil data PDAM Online.");
    } finally {
      setSyncing(false);
    }
  };

  const applySync = async () => {
    if (!data.customer_id) return;
    setApplyingSync(true);
    try {
      const url = `${API}/peta-global/customer/${encodeURIComponent(data.customer_id)}/sync-pdam`;
      const res = await axios.post(url, {}, authHeaders);
      const d = res.data || {};
      if (d.synced) {
        toast.success(d.message || "Berhasil sinkron dari PDAM Online.");
        // Update local form state with new values
        if (d.customer) {
          onChange({ ...data, ...d.customer });
        }
        // Reload pins on map
        if (onReload) onReload();
      } else {
        toast.info(d.message || "Data sudah sinkron.");
      }
      setSyncPreview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal sinkron.");
    } finally {
      setApplyingSync(false);
    }
  };

  // Compress + load image to base64
  const handlePhotoFile = (file) => {
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxSide = 1280;
      let w = img.width, h = img.height;
      if (w > h && w > maxSide) { h = Math.round(h * maxSide / w); w = maxSide; }
      else if (h > maxSide) { w = Math.round(w * maxSide / h); h = maxSide; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.78);
      if (dataUrl.length > 1_500_000) {
        const dataUrl2 = canvas.toDataURL("image/jpeg", 0.6);
        onChange({ ...data, _photo_new: dataUrl2 });
      } else {
        onChange({ ...data, _photo_new: dataUrl });
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error("Gagal membaca foto.");
    };
    img.src = url;
  };

  const runInquiry = useCallback(async (cid, forceOnline = false) => {
    const id = (cid || "").trim();
    if (!id || mode !== "add") return;
    if (!forceOnline && id === lastInquiredRef.current) return;
    lastInquiredRef.current = id;
    setInquiring(true);
    setInquiryError("");
    setInquiryInfo(null);
    try {
      const url = `${API}/peta-global/inquiry/${encodeURIComponent(id)}${forceOnline ? "?force_online=true" : ""}`;
      const res = await axios.get(url, authHeaders);
      const d = res.data || {};
      onChange((prev) => {
        const merged = { ...data };
        for (const k of ["nama", "alamat", "wilayah", "rayon", "kecamatan", "golongan", "nomor_telp"]) {
          if (!merged[k] && d[k]) merged[k] = d[k];
        }
        if (d.status) merged.status = d.status;
        return merged;
      });
      setInquiryInfo({ source: d.source, online: !!d.online });
    } catch (err) {
      setInquiryError(err?.response?.data?.detail || "Gagal mencari data pelanggan.");
    } finally {
      setInquiring(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, authHeaders, data, onChange]);

  useEffect(() => {
    if (mode !== "add") return;
    const id = (data?.customer_id || "").trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!id || id.length < 6) return;
    debounceRef.current = setTimeout(() => { runInquiry(id, false); }, 700);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.customer_id, mode]);

  const sourceLabel = {
    master: { text: "Master (Shapefile)", color: "bg-emerald-50 text-emerald-700" },
    bills: { text: "Bills (upload Excel)", color: "bg-cyan-50 text-cyan-700" },
    history: { text: "Riwayat transaksi", color: "bg-blue-50 text-blue-700" },
    pdam_online: { text: "PDAM Online", color: "bg-purple-50 text-purple-700" },
  };

  // Which fields are "locked" (already filled in master + not admin)
  const isLocked = (field) => {
    if (isAdmin) return false;
    if (mode === "add") return false;
    const v = data[field];
    return v != null && String(v).trim() !== "";
  };

  const hasPhoto = !!(data._photo_new || data.photo);
  const photoSrc = data._photo_new || data.photo || "";

  return (
    <>
    <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-3" data-testid="customer-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800">
            {mode === "add" ? "Tambah Pelanggan" : `Edit ${data.customer_id}`}
          </h3>
          <button onClick={onClose} data-testid="close-modal-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {mode === "edit" && !isAdmin && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-900">
              🔒 Field yang sudah terisi ditandai kunci. Untuk mengubahnya, masukkan <b>kode update</b> dari admin di paling bawah.
            </div>
          )}

          <Field label="ID Pelanggan *">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={data.customer_id || ""}
                  onChange={(e) => set("customer_id", e.target.value)}
                  disabled={mode === "edit"}
                  className="font-mono pr-9"
                  data-testid="modal-cid-input"
                  placeholder="Ketik ID, data otomatis terisi..."
                />
                {inquiring && (
                  <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-cyan-600" />
                )}
              </div>
              {mode === "add" && (
                <Button type="button" size="sm" variant="outline"
                  onClick={() => runInquiry(data.customer_id, true)}
                  disabled={inquiring || !data.customer_id}
                  className="gap-1 text-cyan-700 border-cyan-300"
                  title="Paksa cek online PDAM"
                  data-testid="force-online-btn">
                  {inquiring ? <Loader2 className="w-3 h-3 animate-spin" /> : "🔄"} Online
                </Button>
              )}
            </div>
            {mode === "add" && inquiryInfo && (
              <div className={`mt-1.5 text-[11px] px-2 py-1 rounded inline-flex items-center gap-1 ${sourceLabel[inquiryInfo.source]?.color || "bg-slate-50 text-slate-700"}`}>
                ✓ Data dari: <b>{sourceLabel[inquiryInfo.source]?.text || inquiryInfo.source}</b>
                {inquiryInfo.online && <span className="ml-1">⚡ live</span>}
              </div>
            )}
            {mode === "add" && inquiryError && (
              <div className="mt-1.5 text-[11px] px-2 py-1 rounded bg-amber-50 text-amber-700 inline-block">
                ⚠️ {inquiryError}
              </div>
            )}
          </Field>

          {/* Sinkron dari PDAM Tebo Online (edit mode only) */}
          {mode === "edit" && (
            <div className="rounded-lg border border-cyan-200 bg-cyan-50/50 p-2.5">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="text-xs font-semibold text-cyan-900 flex items-center gap-1">
                  🔄 Sinkron dari PDAM Tebo Online
                </div>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={runSyncPreview}
                  disabled={syncing || applyingSync || !data.customer_id}
                  className="h-7 px-2 text-xs border-cyan-400 text-cyan-700 hover:bg-cyan-100"
                  data-testid="sync-preview-btn"
                >
                  {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Cek Data"}
                </Button>
              </div>
              {data.last_pdam_sync_at && !syncPreview && !syncError && (
                <div className="text-[10.5px] text-cyan-800">
                  Terakhir disinkron: {new Date(data.last_pdam_sync_at).toLocaleString("id-ID")}
                </div>
              )}
              {!syncPreview && !syncError && !data.last_pdam_sync_at && (
                <div className="text-[10.5px] text-cyan-700">
                  Klik <b>Cek Data</b> untuk membandingkan data lokal dengan data resmi PDAM Tirta Muaro Tebo (Status, Golongan, Nama, Alamat).
                </div>
              )}
              {syncError && (
                <div className="text-[11px] px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200" data-testid="sync-error">
                  ⚠️ {syncError}
                </div>
              )}
              {syncPreview && (
                <div className="space-y-2 mt-1">
                  {syncPreview.in_sync ? (
                    <div className="text-[11.5px] px-2 py-1.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1" data-testid="sync-in-sync">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Data lokal sudah sinkron dengan PDAM Online.
                    </div>
                  ) : (
                    <div className="space-y-1" data-testid="sync-diff-list">
                      <div className="text-[11px] text-cyan-900 font-semibold">Perbedaan ditemukan:</div>
                      {Object.entries(syncPreview.changes).map(([field, c]) => (
                        <div key={field} className="text-[11px] bg-white rounded border border-cyan-200 px-2 py-1.5">
                          <div className="text-slate-500 capitalize mb-0.5">{field}:</div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 line-through">{c.old || "(kosong)"}</span>
                            <span className="text-slate-400">→</span>
                            <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">{c.new}</span>
                          </div>
                        </div>
                      ))}
                      {syncPreview.pdam_data?.totaltagihan > 0 && (
                        <div className="text-[10.5px] text-slate-600">
                          ℹ️ Total tagihan PDAM saat ini: <b>Rp {Number(syncPreview.pdam_data.totaltagihan).toLocaleString("id-ID")}</b>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => setSyncPreview(null)}
                      disabled={applyingSync}
                      className="h-7 px-2 text-xs"
                      data-testid="sync-cancel-btn"
                    >
                      Tutup
                    </Button>
                    {!syncPreview.in_sync && (
                      <Button
                        type="button" size="sm"
                        onClick={applySync}
                        disabled={applyingSync}
                        className="h-7 px-2 text-xs bg-cyan-600 hover:bg-cyan-700 text-white gap-1"
                        data-testid="sync-apply-btn"
                      >
                        {applyingSync ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Terapkan
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Photo section — plain div (NOT <label>/Field) so tapping the image never triggers the hidden file inputs */}
          <div className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">{`Foto Rumah ${isLocked("photo") ? "🔒" : ""}`}</div>
            <div className="space-y-2">
              {hasPhoto && (
                <div className="relative">
                  <img src={photoSrc} alt="foto rumah"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPhotoZoom(true); }}
                    className="w-full h-44 object-cover rounded-lg border border-slate-200 cursor-zoom-in"
                    data-testid="photo-zoom-trigger" />
                  <span className="absolute bottom-1 right-1 bg-black/55 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 pointer-events-none">
                    <ZoomIn className="w-3 h-3" /> Ketuk untuk perbesar
                  </span>
                  {data._photo_new && (
                    <span className="absolute top-1 right-1 bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                      Baru
                    </span>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <input ref={photoCamRef} type="file" accept="image/*" capture="environment"
                  className="hidden" onChange={(e) => handlePhotoFile(e.target.files?.[0])}
                  data-testid="photo-camera-input" />
                <input ref={photoGalRef} type="file" accept="image/*"
                  className="hidden" onChange={(e) => handlePhotoFile(e.target.files?.[0])}
                  data-testid="photo-gallery-input" />
                <Button type="button" size="sm" variant="outline"
                  onClick={() => photoCamRef.current?.click()}
                  className="flex-1 gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  data-testid="photo-camera-btn">
                  📷 Kamera
                </Button>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => photoGalRef.current?.click()}
                  className="flex-1 gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="photo-gallery-btn">
                  🖼️ Galeri
                </Button>
              </div>
              {isLocked("photo") && (
                <div className="text-[11px] text-amber-700">
                  🔒 Foto sudah ada. Untuk ganti foto, masukkan kode admin di bawah.
                </div>
              )}
            </div>
          </div>

          <Field label={`Nama ${isLocked("nama") ? "🔒" : ""}`}>
            <Input value={data.nama || ""} onChange={(e) => set("nama", e.target.value)} data-testid="modal-nama-input" />
          </Field>
          <Field label={`Alamat ${isLocked("alamat") ? "🔒" : ""}`}>
            <Input value={data.alamat || ""} onChange={(e) => set("alamat", e.target.value)} data-testid="modal-alamat-input" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Wilayah">
              <Input value={data.wilayah || ""} onChange={(e) => set("wilayah", e.target.value)} data-testid="modal-wilayah-input" />
            </Field>
            <Field label="Rayon">
              <Input value={data.rayon || ""} onChange={(e) => set("rayon", e.target.value)} data-testid="modal-rayon-input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Kecamatan">
              <Input value={data.kecamatan || ""} onChange={(e) => set("kecamatan", e.target.value)} data-testid="modal-kecamatan-input" />
            </Field>
            <Field label="Golongan">
              <Input value={data.golongan || ""} onChange={(e) => set("golongan", e.target.value)} data-testid="modal-golongan-input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={`Status ${isLocked("status") ? "🔒" : ""}`}>
              <select value={data.status || "Aktif"} onChange={(e) => set("status", e.target.value)}
                className="w-full border border-slate-200 rounded px-2 py-2 text-sm bg-white"
                data-testid="modal-status-select">
                <option value="Aktif">Aktif</option>
                <option value="Non Aktif">Non Aktif</option>
              </select>
            </Field>
            <Field label={`No. Telp ${isLocked("nomor_telp") ? "🔒" : ""}`}>
              <Input value={data.nomor_telp || ""} onChange={(e) => set("nomor_telp", e.target.value)} data-testid="modal-telp-input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={`Latitude * ${isLocked("latitude") ? "🔒" : ""}`}>
              <Input type="number" step="any" value={data.latitude ?? ""}
                onChange={(e) => set("latitude", e.target.value === "" ? null : parseFloat(e.target.value))}
                className="font-mono text-xs" data-testid="modal-lat-input" />
            </Field>
            <Field label={`Longitude * ${isLocked("longitude") ? "🔒" : ""}`}>
              <Input type="number" step="any" value={data.longitude ?? ""}
                onChange={(e) => set("longitude", e.target.value === "" ? null : parseFloat(e.target.value))}
                className="font-mono text-xs" data-testid="modal-lng-input" />
            </Field>
          </div>

          {/* Admin: Generate Update Code (for petugas/kasir to unlock edit) */}
          {mode === "edit" && isAdmin && (
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="text-xs font-semibold text-purple-900 flex items-center gap-1">
                  🔑 Kode Update untuk Petugas
                </div>
                <Button type="button" size="sm"
                  onClick={() => onGenerateCode && onGenerateCode(data.customer_id)}
                  className="h-7 px-2 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="modal-generate-code-btn">
                  Buat Kode Baru
                </Button>
              </div>
              {data.update_code_info?.code ? (
                <div className="bg-white border border-purple-300 rounded p-2">
                  <div className="text-[10px] text-slate-500 mb-0.5">Kode aktif:</div>
                  <div className="font-mono text-lg font-bold tracking-widest text-purple-700 select-all" data-testid="modal-active-code">
                    {data.update_code_info.code}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Berlaku sampai: {data.update_code_info.expires_at ? new Date(data.update_code_info.expires_at).toLocaleString("id-ID") : "-"}
                  </div>
                </div>
              ) : (
                <div className="text-[11px] text-purple-800">
                  Belum ada kode aktif. Klik <b>Buat Kode Baru</b> lalu berikan ke petugas untuk membuka kunci edit pelanggan ini (berlaku 15 menit).
                </div>
              )}
            </div>
          )}

          {/* Update code field (only for petugas in edit mode) */}
          {mode === "edit" && !isAdmin && (
            <Field label="🔑 Kode Update (dari Admin)">
              <Input value={data.update_code || ""}
                onChange={(e) => set("update_code", e.target.value.toUpperCase())}
                placeholder="6 karakter (kosongkan kalau hanya isi field kosong)"
                maxLength={8}
                className="font-mono uppercase tracking-wider"
                data-testid="modal-update-code-input" />
              <div className="text-[11px] text-slate-500 mt-1">
                Wajib jika ingin mengubah field yang sudah terisi (Nama, Alamat, Telp, Status, GPS, Foto).
              </div>
            </Field>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex flex-col gap-2 sticky bottom-0 bg-white">
          {mode === "add" && !inquiryInfo && !isReadOnly && (
            <div className="text-[11px] px-2 py-1.5 rounded bg-amber-50 text-amber-800 border border-amber-200" data-testid="add-blocked-hint">
              ⚠️ Titik hanya bisa disimpan jika data pelanggan ditemukan di PDAM. Ketik/cek ID yang valid dulu.
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="modal-cancel-btn">Tutup</Button>
            {!isReadOnly && (
              <Button onClick={onSave} disabled={saving || (mode === "add" && !inquiryInfo)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="modal-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Simpan
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    {showPhotoZoom && hasPhoto && (
      <div className="fixed inset-0 z-[2200] bg-black/90 flex items-center justify-center p-4"
        onClick={() => setShowPhotoZoom(false)} data-testid="photo-zoom-overlay">
        <button
          onClick={(e) => { e.stopPropagation(); setShowPhotoZoom(false); }}
          className="absolute top-4 right-4 text-white bg-white/15 hover:bg-white/25 rounded-full p-2"
          data-testid="photo-zoom-close-btn">
          <X className="w-6 h-6" />
        </button>
        <img src={photoSrc} alt="foto rumah besar"
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain rounded-lg" />
      </div>
    )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

// Modal: bulk-sync ALL customers from PDAM Tirta Muaro Tebo online (admin-only).
// Polls /api/admin/peta-global/sync-all/status every 2s while running.
function BulkSyncModal({ authHeaders, facets, onClose, onDone }) {
  const [scope, setScope] = useState({ wilayah: "", rayon: "", kecamatan: "" });
  const [state, setState] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/peta-global/sync-all/status`, authHeaders);
      setState(res.data);
      if (!res.data.running && res.data.finished_at) {
        // Reload pins one more time
        if (onDone) onDone();
      }
    } catch {
      /* ignore */
    }
  }, [authHeaders, onDone]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  const startSync = async () => {
    setStarting(true);
    try {
      const params = new URLSearchParams();
      if (scope.wilayah) params.set("wilayah", scope.wilayah);
      if (scope.rayon) params.set("rayon", scope.rayon);
      if (scope.kecamatan) params.set("kecamatan", scope.kecamatan);
      const q = params.toString();
      const url = `${API}/admin/peta-global/sync-all/start${q ? "?" + q : ""}`;
      await axios.post(url, {}, authHeaders);
      toast.success("Sinkron massal dimulai.");
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memulai sinkron.");
    } finally {
      setStarting(false);
    }
  };

  const cancelSync = async () => {
    try {
      await axios.post(`${API}/admin/peta-global/sync-all/cancel`, {}, authHeaders);
      toast.info("Permintaan batal dikirim.");
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membatalkan.");
    }
  };

  const isRunning = state?.running;
  const total = state?.total || 0;
  const processed = state?.processed || 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return (
    <div className="fixed inset-0 z-[2100] bg-black/50 flex items-center justify-center p-3" data-testid="bulk-sync-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            🔄 Sinkron Massal dari PDAM Tebo Online
          </h3>
          <button onClick={onClose} disabled={isRunning} data-testid="close-bulk-sync-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[12px] text-slate-700 bg-cyan-50 border border-cyan-200 rounded p-2.5">
            Akan mengambil data terbaru (<b>Status, Golongan, Nama, Alamat</b>) dari PDAM Tirta Muaro Tebo dan memperbarui master Peta Global secara otomatis.
            <div className="mt-1 text-amber-700 text-[11px]">
              ⚠️ Proses akan memakan waktu (±1–3 detik per pelanggan). Anda bisa membatalkan kapan saja.
            </div>
          </div>

          {!isRunning && (
            <>
              <div className="text-xs font-semibold text-slate-700">Cakupan (opsional, kosongkan = semua):</div>
              <div className="grid grid-cols-1 gap-2">
                <FilterSelect label="Wilayah" value={scope.wilayah}
                  options={facets?.wilayah || []}
                  onChange={(v) => setScope(s => ({ ...s, wilayah: v }))}
                  testid="bulk-scope-wilayah" />
                <FilterSelect label="Rayon" value={scope.rayon}
                  options={facets?.rayon || []}
                  onChange={(v) => setScope(s => ({ ...s, rayon: v }))}
                  testid="bulk-scope-rayon" />
                <FilterSelect label="Kecamatan" value={scope.kecamatan}
                  options={facets?.kecamatan || []}
                  onChange={(v) => setScope(s => ({ ...s, kecamatan: v }))}
                  testid="bulk-scope-kecamatan" />
              </div>
            </>
          )}

          {state && (state.started_at || state.running) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-700">Progress</span>
                <span className="font-mono text-cyan-700">{processed} / {total} ({pct}%)</span>
              </div>
              <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-1.5 text-center" data-testid="bulk-stat-synced">
                  <div className="text-slate-500">Disinkron</div>
                  <div className="font-bold text-emerald-700">{state.synced}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-1.5 text-center" data-testid="bulk-stat-nochange">
                  <div className="text-slate-500">Sudah Sama</div>
                  <div className="font-bold text-slate-700">{state.no_change}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-1.5 text-center" data-testid="bulk-stat-failed">
                  <div className="text-slate-500">Gagal</div>
                  <div className="font-bold text-red-700">{state.failed}</div>
                </div>
              </div>
              {isRunning && state.current_id && (
                <div className="text-[10.5px] text-slate-500 font-mono truncate">
                  Sedang proses: {state.current_id}
                </div>
              )}
              {!isRunning && state.finished_at && (
                <div className="text-[11px] px-2 py-1.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                  ✓ Selesai pada {new Date(state.finished_at).toLocaleString("id-ID")}
                </div>
              )}
              {state.errors && state.errors.length > 0 && (
                <details className="text-[10.5px] text-slate-600">
                  <summary className="cursor-pointer text-red-600">
                    Lihat error ({state.errors.length})
                  </summary>
                  <div className="mt-1 max-h-32 overflow-y-auto bg-red-50 border border-red-200 rounded p-1.5 space-y-0.5">
                    {state.errors.slice(0, 30).map((e, i) => (
                      <div key={i} className="font-mono text-[10px]">
                        <b>{e.id}</b>: {e.error}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          {!isRunning ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="bulk-close-btn">Tutup</Button>
              <Button onClick={startSync} disabled={starting}
                className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1"
                data-testid="bulk-start-btn">
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : "🔄"} Mulai Sinkron
              </Button>
            </>
          ) : (
            <Button onClick={cancelSync} variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="bulk-cancel-btn">
              Batalkan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Compress an image File to a JPEG data URL (for delivery proof photos)
const compressDeliveryPhoto = (file, maxWidth = 800, quality = 0.75) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Modal: save pins + prefetch the current map area's tiles for offline use.
function OfflineDownloadModal({ mapRef, customers, base, baseLayer, offlineMeta, onSavedMeta, onClose, scopedBounds = null, scopedLabel = null }) {
  const [extraZoom, setExtraZoom] = useState(2);
  const [tileEstimate, setTileEstimate] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [savedTiles, setSavedTiles] = useState(0);
  const [pinsSaved, setPinsSaved] = useState(false);

  const getBounds = useCallback(() => {
    if (scopedBounds) return scopedBounds;
    const m = mapRef.current;
    if (!m) return null;
    const b = m.getBounds();
    return { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() };
  }, [mapRef, scopedBounds]);

  const zoomRange = useCallback(() => {
    if (scopedBounds) {
      // Derive a base zoom that fits the whole list area, then add detail levels.
      const latSpan = Math.max(scopedBounds.north - scopedBounds.south, 1e-4);
      const lngSpan = Math.max(scopedBounds.east - scopedBounds.west, 1e-4);
      const span = Math.max(latSpan, lngSpan);
      let baseZ = Math.floor(Math.log2(360 / span)) - 1;
      baseZ = Math.max(12, Math.min(16, baseZ));
      const maxZ = Math.min(baseZ + extraZoom + 1, baseLayer.maxZoom);
      return { minZ: baseZ, maxZ };
    }
    const m = mapRef.current;
    const z = m ? Math.floor(m.getZoom()) : 15;
    const minZ = z;
    const maxZ = Math.min(z + extraZoom, baseLayer.maxZoom);
    return { minZ, maxZ };
  }, [mapRef, extraZoom, baseLayer.maxZoom, scopedBounds]);

  useEffect(() => {
    const b = getBounds();
    if (!b) return;
    const { minZ, maxZ } = zoomRange();
    setTileEstimate(countAreaTiles(b, minZ, maxZ));
  }, [getBounds, zoomRange]);

  useEffect(() => { getCachedTileCount().then(setSavedTiles); }, []);

  const handleSavePins = async () => {
    const n = await savePetaCustomers(customers);
    setPinsSaved(true);
    const meta = await getPetaMeta();
    onSavedMeta && onSavedMeta(meta);
    toast.success(`${n.toLocaleString()} titik pelanggan disimpan untuk offline.`);
  };

  const handleDownloadTiles = async () => {
    const b = getBounds();
    if (!b) { toast.error("Peta belum siap."); return; }
    const { minZ, maxZ } = zoomRange();
    const est = countAreaTiles(b, minZ, maxZ);
    if (est > 4000) {
      if (!window.confirm(`Area ini butuh ~${est.toLocaleString()} gambar peta (akan dibatasi 4000). Lanjut?`)) return;
    }
    setBusy(true);
    setProgress({ done: 0, total: est });
    try {
      // Save pins too if not yet saved
      if (!pinsSaved) await handleSavePins();
      const r = await prefetchArea(
        b, minZ, maxZ, baseLayer.urls[0], baseLayer.subdomains,
        (done, total) => setProgress({ done, total }),
      );
      const cnt = await getCachedTileCount();
      setSavedTiles(cnt);
      toast.success(`Area peta tersimpan offline: ${r.done.toLocaleString()} gambar (gagal ${r.failed}).`);
    } catch (err) {
      toast.error(err?.message || "Gagal mengunduh area peta.");
    } finally {
      setBusy(false);
    }
  };

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[2100] bg-black/60 flex items-center justify-center p-3" data-testid="offline-download-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-sky-600 to-cyan-600 text-white p-4 flex items-center justify-between sticky top-0">
          <h2 className="font-bold text-base flex items-center gap-2">
            <HardDriveDownload className="w-5 h-5" /> Simpan Peta Offline
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-offline-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-slate-600 leading-relaxed">
            Simpan data titik pelanggan & gambar peta area yang sedang tampil, supaya peta tetap bisa dibuka
            tanpa internet dan tidak nge-lag saat dibuka ulang.
          </p>

          {scopedLabel && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800" data-testid="offline-scoped-banner">
              📍 Khusus Antar Surat: <b>{scopedLabel}</b> — hanya titik & area daftar ini yang disimpan ({customers.length.toLocaleString()} titik).
            </div>
          )}

          {/* Pins */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-emerald-800">1. Data Titik Pelanggan</div>
                <div className="text-[11px] text-emerald-700">
                  {customers.length.toLocaleString()} titik termuat
                  {offlineMeta?.savedAt && <> · tersimpan {new Date(offlineMeta.savedAt).toLocaleString("id-ID")}</>}
                </div>
              </div>
              <Button size="sm" onClick={handleSavePins} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="save-pins-btn">
                {pinsSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Tiles */}
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 space-y-2">
            <div className="text-sm font-semibold text-sky-800">2. Gambar Peta ({baseLayer.name}) — Area Tampil</div>
            <div className="text-[11px] text-sky-700">
              Memperbesar detail offline: +{extraZoom} level zoom dari tampilan sekarang.
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">Detail:</span>
              {[1, 2, 3].map((z) => (
                <button key={z} onClick={() => setExtraZoom(z)} disabled={busy}
                  className={`px-3 py-1 rounded-full text-xs border ${extraZoom === z ? "bg-sky-600 text-white border-sky-700" : "bg-white text-sky-700 border-sky-300"}`}
                  data-testid={`tile-zoom-${z}-btn`}>
                  +{z}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500">
              Perkiraan ~{tileEstimate.toLocaleString()} gambar · sudah tersimpan {savedTiles.toLocaleString()}
            </div>
            {busy && (
              <div className="space-y-1">
                <div className="h-2 bg-sky-100 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-600 transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[11px] text-sky-700 text-right">{progress.done.toLocaleString()} / {progress.total.toLocaleString()} ({pct}%)</div>
              </div>
            )}
            <Button onClick={handleDownloadTiles} disabled={busy}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white gap-1" data-testid="download-tiles-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {busy ? "Mengunduh area..." : "Unduh Area Ini"}
            </Button>
          </div>

          <p className="text-[11px] text-slate-400 leading-relaxed">
            Tips: geser & atur zoom peta ke area kerja Anda sebelum menekan "Unduh Area Ini".
            Ulangi untuk area lain bila perlu.
          </p>
        </div>
      </div>
    </div>
  );
}

// Inline editor for an Antar Surat delivery item — same flow as the AntarSurat
// detail page, but mounted on top of the map so officers don't have to leave it.
function DeliveryEditModal({ item, listId, authToken, onClose, onUpdated }) {
  const initPhotos = Array.isArray(item.photos) && item.photos.filter(Boolean).length
    ? item.photos.filter(Boolean).slice(0, 5)
    : (item.photo ? [item.photo] : []);
  const [status, setStatus] = useState(item.status || "pending");
  const [note, setNote] = useState(item.note || "");
  const [photos, setPhotos] = useState(initPhotos);
  const initialPhotosRef = useRef(initPhotos);
  const [promisedDate, setPromisedDate] = useState(item.promised_date || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 5 - photos.length;
    if (room <= 0) { toast.error("Maksimum 5 foto per ID."); return; }
    const take = files.slice(0, room);
    if (files.length > room) toast.info(`Hanya ${room} foto ditambahkan (maks 5).`);
    try {
      const out = [];
      for (const f of take) {
        if (!f.type.startsWith("image/")) continue;
        out.push(await compressDeliveryPhoto(f, 800, 0.75));
      }
      setPhotos((prev) => [...prev, ...out].slice(0, 5));
    } catch {
      toast.error("Gagal memproses foto.");
    }
  };

  const handleSave = async () => {
    if (status === "promised_pay" && !promisedDate) {
      toast.error("Pilih tanggal janji bayar terlebih dulu.");
      return;
    }
    setSaving(true);
    const promised = status === "promised_pay" ? promisedDate : "";
    const base = { status, note, promised_date: promised };
    const initial = initialPhotosRef.current || [];
    const added = photos.filter((p) => !initial.includes(p));
    const removed = initial.filter((p) => !photos.includes(p));
    if (!navigator.onLine) {
      const offlinePayload = { ...base, photos };
      await enqueueDeliveryEdit(listId, item.customer_id, offlinePayload);
      toast.success("Tersimpan offline. Akan otomatis tersinkron saat online.");
      onUpdated({ customer_id: item.customer_id, ...offlinePayload, _pendingSync: true }, null);
      setSaving(false);
      return;
    }
    // Online → send only the photo DELTA so concurrent editors don't clobber photos.
    const payload = { ...base };
    if (added.length) payload.photos_add = added;
    if (removed.length) payload.photos_remove = removed;
    const tid = toast.loading("Menyimpan...");
    try {
      const res = await axios.patch(
        `${API}/delivery-lists/${listId}/items/${item.customer_id}`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Status antar surat tersimpan.", { id: tid });
      onUpdated(res.data.item, res.data.counts);
    } catch (err) {
      if (!err?.response) {
        const offlinePayload = { ...base, photos };
        await enqueueDeliveryEdit(listId, item.customer_id, offlinePayload);
        toast.success("Koneksi terputus — disimpan offline & akan tersinkron otomatis.", { id: tid });
        onUpdated({ customer_id: item.customer_id, ...offlinePayload, _pendingSync: true }, null);
      } else {
        toast.error(err?.response?.data?.detail || "Gagal menyimpan.", { id: tid });
      }
    } finally {
      setSaving(false);
    }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-[2100] bg-black/60 flex items-center justify-center p-3" data-testid="map-delivery-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> Status Antar Surat
            </h2>
            <p className="text-xs opacity-90 font-mono truncate">{item.customer_id} · {item.nama || "-"}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="map-close-delivery-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "pending", label: "Belum", icon: Circle, color: "border-rose-300 text-rose-700 hover:bg-rose-50", active: "bg-rose-600 border-rose-700" },
                { key: "delivered", label: "Sudah", icon: CheckCircle2, color: "border-emerald-300 text-emerald-700 hover:bg-emerald-50", active: "bg-emerald-600 border-emerald-700" },
                { key: "not_found", label: "Tidak Ketemu", icon: XCircle, color: "border-slate-300 text-slate-600 hover:bg-slate-50", active: "bg-slate-600 border-slate-700" },
                { key: "promised_pay", label: "Janji Bayar", icon: Calendar, color: "border-sky-300 text-sky-700 hover:bg-sky-50", active: "bg-sky-600 border-sky-700" },
                { key: "lunas", label: "Lunas", icon: HandCoins, color: "border-teal-300 text-teal-700 hover:bg-teal-50", active: "bg-teal-600 border-teal-700" },
                { key: "putus", label: "Putus", icon: Scissors, color: "border-purple-300 text-purple-700 hover:bg-purple-50", active: "bg-purple-600 border-purple-700" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setStatus(opt.key)}
                  className={`px-2 py-3 rounded-lg border-2 flex flex-col items-center gap-1 text-xs font-semibold transition ${
                    status === opt.key ? `${opt.active} text-white shadow-sm` : `bg-white ${opt.color}`
                  }`}
                  data-testid={`map-status-${opt.key}-btn`}
                >
                  <opt.icon className="w-5 h-5" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {status === "promised_pay" && (
            <div data-testid="map-promised-date-section">
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Tanggal Janji Bayar <span className="text-rose-600">*</span>
              </label>
              <Input
                type="date"
                value={promisedDate}
                min={todayIso}
                onChange={(e) => setPromisedDate(e.target.value)}
                className="text-sm"
                data-testid="map-promised-date-input"
              />
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Catatan (opsional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Mis: Diterima istri, ditempel di pintu, dll."
              data-testid="map-delivery-note-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">
              Foto Bukti (opsional) <span className="text-slate-400 font-normal">· {photos.length}/5</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((ph, idx) => (
                <div className="relative" key={idx}>
                  <img src={ph} alt={`bukti ${idx + 1}`} className="w-full h-24 object-cover bg-slate-100 rounded-lg border" data-testid={`map-photo-preview-${idx}`} />
                  <button
                    className="absolute top-1 right-1 bg-rose-600 text-white rounded-full p-1 shadow"
                    onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
                    data-testid={`map-remove-photo-${idx}-btn`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="outline" className="flex-1 gap-1 h-full min-h-[3rem]"
                    onClick={() => { fileRef.current.setAttribute("capture", "environment"); fileRef.current.click(); }}
                    data-testid="map-add-photo-camera-btn">
                    <Camera className="w-3.5 h-3.5" /> Kamera
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 gap-1"
                    onClick={() => { fileRef.current.removeAttribute("capture"); fileRef.current.click(); }}
                    data-testid="map-add-photo-gallery-btn">
                    <ImageIcon className="w-3.5 h-3.5" /> Galeri
                  </Button>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handlePhotoSelect} className="hidden" />
          </div>
        </div>

        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving} data-testid="map-cancel-delivery-btn">
            Batal
          </Button>
          <Button className="flex-1 gap-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={handleSave} disabled={saving} data-testid="map-save-delivery-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Simpan
          </Button>
        </div>
      </div>
    </div>
  );
}
